//
// # Workspace
//
// The top-level owner of Editor instances in the IDE.
// Tracks open editors via IdentMap, manages tab state,
// and provides an observable snapshot for React to subscribe to.
//
// Editors communicate with the Workspace through the Platform interface
// (via WorkspacePlatform). Messages from editors are tagged with their Ident
// and routed accordingly.
//

import typia from "typia";
import { logger, LoadKind, Result, validate as v, Context } from "CoreUtils";
import { DockviewApi, IDockviewPanel } from "dockview-core";
import { Editor, Theme } from "EditorCore";
import {
  Ident,
  IdentMap,
  SchematicChange,
  randomId,
  layout,
} from "GenAlphaModel";
import { SrvModel, type SrvRuleDescriptor } from "./services/SrvModel";
import {
  ActiveDiagramObserver,
  ActiveDiagramSubject,
} from "./services/activeDiagram";
import { schema } from "GenAlphaSchema";
import { AgentTreeNode } from "AgentRunTreeSchema";
import type { DepotTreeNode } from "Depot";
import {
  type SymbolInfo,
  type GeneratorParamSpec,
  type CreateInstanceResult,
  type InstanceDetails,
  type ImageAttachment,
  PromptResponseWire,
  ListSrvRulesResult,
} from "ServerInterface";
import {
  AlphaClient,
  DesignLoadMessage,
  DesignChangesMessage,
  type ProgressCallback,
} from "./client";
import { CommandCenter, registerBuiltinCommands } from "./commandCenter";
import { createRequestContext } from "./contextHelpers";
import { extractErrorMessage, hasErrorWhy } from "./errorUtil";
import { USE_BATCH_HIERARCHY_LOAD, WorkspacePlatform } from "./platform";
import type { LvsResult, LvsError } from "./components/LvsPanel/types";
import type { DrcResult, DrcError } from "./components/DrcPanel/types";
import type { IvaResult } from "./components/IvaPanel/types";
import {
  userPreferences,
  GridIntensity,
  IdeThemeMode,
} from "./userPreferences";
import {
  PexResultResponse,
  type ParasiticsResult,
} from "./components/ParasiticsPanel/types";
import {
  ActiveDesignInfo,
  AddExprLabelDialogState,
  AddLayoutPinDialogState,
  AddSymbolPinDialogState,
  ConsoleLogEntry,
  ConsoleLogLevel,
  ConstraintDialogState,
  DesignNameDialogMode,
  DummyDeviceDialogState,
  EditExprLabelDialogState,
  EditLayoutPinDialogState,
  EditSymbolPinDialogState,
  EditWireLabelDialogState,
  EditRouteWidthDialogState,
  TextAnnotationDialogState,
  GenerateFromSourceDialogResult,
  RecoverSessionDialogState,
  OverwriteDialogState,
  GenerateFromSourceDialogState,
  InstanceDialogState,
  LayoutPickerState,
  LinkageInfo,
  OaTechInfo,
  SaveAsDestination,
  ManageLinkagesDialogState,
  PinDialogState,
  PinDirection,
  PowerRoutingDialogState,
  ReconnectDialogState,
  RenameDuplicateDialogMode,
  SchematicPickerState,
  StatusMessage,
  TabInfo,
  WorkspaceSnapshot,
} from "./types";
import {
  PropertiesController,
  type PropertiesHost,
} from "./propertiesController";
import {
  SymbolInfoArrayValidator,
  InstanceDetailsValidator,
  GeneratorParamSpecOrNullValidator,
} from "./serverValidators";

// Validator for the list-content response — validated at the data boundary.
const IdentListValidator = {
  validator: new v.Validator(typia.createValidate<schema.Ident[]>()),
};

// Validator for a single Ident from design-load events.
const IdentValidator = {
  validator: new v.Validator(typia.createValidate<schema.Ident>()),
};

// Validators for the depot-copy / rename responses — validated at the data
// boundary so the returned ident is trusted before we open/refresh on it.
const DepotCopyResultValidator = {
  validator: new v.Validator(
    typia.createValidate<{ copiedIdent: schema.HierPathIdent }>()
  ),
};
const RenameResultValidator = {
  validator: new v.Validator(
    typia.createValidate<{ renamedIdent: schema.HierPathIdent }>()
  ),
};

// Validator for the list-content-tree response.
const DepotTreeValidator = {
  validator: new v.Validator(typia.createValidate<DepotTreeNode[]>()),
};

// Validator for string array responses (PDK list, stack list).
const StringArrayValidator = {
  validator: new v.Validator(typia.createValidate<string[]>()),
};

// Validator for create-instance response.
const CreateInstanceResultValidator = {
  validator: new v.Validator(typia.createValidate<CreateInstanceResult>()),
};

// Validator for get-design-linkages response.
const LinkageInfoValidator = {
  validator: new v.Validator(typia.createValidate<LinkageInfo>()),
};

// OpenAccess catalog types for client-side tree rendering.
// Simple types that don't require schema imports.
// Re-exported so existing importers of this module keep working.
export type { SaveAsDestination } from "./types";
export { saveAsTargetIdent } from "./components/DesignExplorer/pathInput";
import { saveAsTargetIdent } from "./components/DesignExplorer/pathInput";

export interface OACell {
  name: string;
  views: string[];
}
export interface OALibrary {
  name: string;
  cells: OACell[];
}
export interface OACatalog {
  libraries: OALibrary[];
}
const OACatalogValidator = {
  validator: new v.Validator(typia.createValidate<OACatalog>()),
};
const OaTechInfoValidator = {
  validator: new v.Validator(typia.createValidate<OaTechInfo>()),
};

// An entry tracking an Editor instance and its associated state.
interface EditorEntry {
  readonly ident: Ident;
  readonly editor: Editor;
  readonly platform: WorkspacePlatform;
  // The container div that holds this editor's canvas.
  // The Editor creates its canvas inside this div at construction time.
  // React attaches/detaches this div from the DOM as tabs switch.
  readonly container: HTMLDivElement;
}

type SnapshotListener = () => void;

export class Workspace implements ActiveDiagramSubject {
  private readonly editors = new IdentMap<EditorEntry>();
  private readonly tabOrder: Ident[] = [];
  private activeIdent: Ident | null = null;

  // Observers notified when the active diagram changes (observer pattern). See
  // setActiveIdent / subscribeActiveDiagram. SrvModel registers here to clear
  // stale results on a switch.
  private readonly activeDiagramObservers = new Set<ActiveDiagramObserver>();

  // The current snapshot for React's useSyncExternalStore.
  // Replaced (not mutated) on every state change.
  private snapshot: WorkspaceSnapshot = {
    tabs: [],
    activeIdent: null,
    generateFromSourceDialog: null,
    overwriteDialog: null,
    designTreeVersion: 0,
    contentVersion: 0,
    constraintsVersion: 0,
    hierarchyVersion: 0,
    statusMessage: null,
    commandPaletteOpen: false,
    designNameDialog: null,
    renameDuplicateDialog: null,
    genSymbolFromSchDialogOpen: false,
    graphicsCatalogueDialogOpen: false,
    saveAsDialogOpen: false,
    reportIssueDialogOpen: false,
    deleteDesignIdent: null,
    leftPanelVisible: true,
    rightPanelVisible: true,
    bottomPanelVisible: true,
    isPromptInFlight: false,
    revealInDepotIdent: null,
    hierarchyRevealTarget: null,
    instanceDialog: null,
    schematicPickerDialog: null,
    layoutPickerDialog: null,
    activeDesignInfo: null,
    addSymbolPinDialog: null,
    addExprLabelDialog: null,
    editSymbolPinDialog: null,
    editExprLabelDialog: null,
    addLayoutPinDialog: null,
    editLayoutPinDialog: null,
    pinDialog: null,
    editWireLabelDialog: null,
    editRouteWidthDialog: null,
    recoverSessionDialog: null,
    textAnnotationDialog: null,
    manageLinkagesDialog: null,
    constraintDialog: null,
    dummyDeviceDialog: null,
    collapsedPanels: new Set<string>(),
    selectedConstraintMatchIds: new Set(),
    powerRoutingDialog: null,
    isConnected: false,
    consoleLog: [],
    reconnectDialog: null,
    selection: null,
    selectionVersion: 0,
  };
  private readonly listeners = new Set<SnapshotListener>();

  // Dockview API, set by WorkspaceLayout once the dock is ready.
  private dockApi: DockviewApi | null = null;

  // Re-entry guard: true while workspace.close() is removing a dockview panel,
  // so the onDidRemovePanel listener doesn't double-clean.
  private _closingFromWorkspace = false;

  // Create-layout dialog state (Promise-based).
  // When promptCreateLayout() is called, the dialog opens and the Promise
  // resolves when the user confirms or cancels.
  private generateFromSourceDialogState: GenerateFromSourceDialogState | null =
    null;
  private generateFromSourceDialogResolve:
    | ((result: GenerateFromSourceDialogResult | null) => void)
    | null = null;

  // Generic confirmation dialog state (Promise-based).
  private overwriteDialogState: OverwriteDialogState | null = null;
  private overwriteDialogResolve: ((result: boolean) => void) | null = null;

  // Latest running-agent hierarchy for the Agents panel, or null when there is
  // no live conversation/tree. Updated by push (setAgentTree, wired from
  // client.onAgentTree) and by pull (fetchAgentTree on open/reconnect).
  //
  // Deliberately NOT part of WorkspaceSnapshot: the tree pushes on every agent
  // lifecycle transition and tool call (~10Hz during a run), and a snapshot
  // bump re-renders every useWorkspaceSnapshot consumer. Like streamed
  // progress, it rides its own subscription so only the Agents panel re-renders.
  private _agentTree: AgentTreeNode | null = null;
  private readonly agentTreeListeners = new Set<
    (tree: AgentTreeNode | null) => void
  >();

  // Incremented when the depot tree changes (design saved/added/removed).
  // DesignExplorer subscribes to this via the snapshot to trigger re-fetch.
  private _designTreeVersion = 0;

  // Incremented when editor content is reloaded (e.g. after extract-topology).
  // ConstraintsPanel subscribes to this via the snapshot to re-fetch constraints.
  private _contentVersion = 0;
  private _constraintsVersion = 0;

  // Incremented on every design edit (add/rename/delete/move) via markDirty.
  // The Object Hierarchy panel subscribes to this via the snapshot to rebuild
  // its top-level entries. Kept separate from _contentVersion so the network
  // re-fetch panels (LVS/DRC/IVA/constraints) don't churn on every edit.
  private _hierarchyVersion = 0;

  // Owns the Properties Panel selection state + edit logic. Workspace delegates
  // its public Properties-Panel methods here and reads selection /
  // selectionVersion from it when building the snapshot.
  readonly properties: PropertiesController;

  // Stringified ident keys for designs with unsaved changes.
  private readonly dirtyIdents = new Set<string>();

  // Pending notifyUIChange() HTTP requests. Save flushes these before writing
  // to depot to prevent the race where "save" arrives before "changes".
  private readonly _pendingChanges = new Set<Promise<unknown>>();

  // Auto-save timer: fires 5 min after the last edit to persist to Depot.
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly autoSaveDelayMs = 300_000;

  // Transient status message shown in the status bar (e.g. "Saved").
  private _statusMessage: StatusMessage | null = null;
  private statusClearTimer: ReturnType<typeof setTimeout> | null = null;

  // Centralized command registry and keyboard shortcut dispatch.
  readonly commandCenter: CommandCenter;

  // Owns the entire TS-side SRV (Schematic Rules Validation) story: state slot,
  // operations, and logger registration. Constructed in the ctor with this
  // workspace's editor/context/toast dependencies wired in.
  readonly srv: SrvModel;

  // Whether the command palette overlay is currently open.
  private _commandPaletteOpen = false;

  // Design-path dialog state ("create" mode). Null when the dialog is closed.
  private _designNameDialog: DesignNameDialogMode | null = null;

  // Rename/Duplicate dialog state for a depot design. Null when closed.
  private _renameDuplicateDialog: RenameDuplicateDialogMode | null = null;

  // Whether the "Generate Symbol from Schematic" dialog is currently open.
  private _genSymbolFromSchDialogOpen = false;

  // Whether the "Graphics Catalogue" dialog is currently open.
  private _graphicsCatalogueDialogOpen = false;

  // Whether the "Save As" dialog is currently open.
  private _saveAsDialogOpen = false;

  // Whether the "Report an Issue" dialog is currently open. See issue #2146.
  private _reportIssueDialogOpen = false;

  // Ident of the design pending deletion confirmation, or null if dialog is closed.
  private _deleteDesignIdent: Ident | null = null;

  // State for the schematic picker dialog, or null when not open.
  private _schematicPickerDialog: SchematicPickerState | null = null;

  // State for the layout picker dialog, or null when not open.
  private _layoutPickerDialog: LayoutPickerState | null = null;

  // State for the "Add Symbol Pin" dialog, or null when not open.
  private _addSymbolPinDialog: AddSymbolPinDialogState | null = null;

  // State for the "Add Expression Label" dialog, or null when not open.
  private _addExprLabelDialog: AddExprLabelDialogState | null = null;

  // State for the "Edit Symbol Pin" dialog, or null when not open.
  private _editSymbolPinDialog: EditSymbolPinDialogState | null = null;

  // State for the "Edit Expression Label" dialog, or null when not open.
  private _editExprLabelDialog: EditExprLabelDialogState | null = null;

  // State for the "Add Layout Pin" dialog, or null when not open.
  private _addLayoutPinDialog: AddLayoutPinDialogState | null = null;

  // State for the "Edit Layout Pin" dialog, or null when not open.
  private _editLayoutPinDialog: EditLayoutPinDialogState | null = null;

  // IDs of constraint matches currently selected in the ConstraintsPanel.
  private _selectedConstraintMatchIds: Set<number> = new Set();

  // LVS verification results, or null if no LVS has been run.
  // Set by the run-lvs tool, read by the LvsPanel.
  private _lvsResult: LvsResult | null = null;

  // DRC verification results, or null if no DRC has been run.
  // Set by the run-drc tool, read by the DrcPanel.
  private _drcResult: DrcResult | null = null;

  // IVA verification results, or null if no IVA has been run.
  // Set by the run-iva tool, read by the IvaPanel.
  private _ivaResult: IvaResult | null = null;

  // PEX (Parasitic Extraction) results for the active layout.
  // Set when runPex() completes successfully.
  private _pexResult: ParasiticsResult | null = null;

  // Whether the WebSocket connection to AlphaServer is currently open.
  // Starts false because the connection is not yet established when
  // Workspace is constructed; flipped true on ws.onopen via AlphaClient's
  // onConnectionEvent callback.
  private _isConnected = false;

  // State for the "Connection Lost — Reload?" modal. Null when the
  // connection is healthy or just briefly disconnected; set when AlphaClient
  // fires onConnectionLost. Idempotent — first reason wins.
  private _reconnectDialog: ReconnectDialogState | null = null;

  // Bounded ring-buffer of console log entries rendered by BottomPanel.
  // Oldest entries drop off when the buffer exceeds CONSOLE_LOG_LIMIT.
  // Cap chosen to hold ~an hour of typical network/editor activity; bump if
  // we start logging higher-frequency events.
  private static readonly CONSOLE_LOG_LIMIT = 500;
  private _consoleLog: ReadonlyArray<ConsoleLogEntry> = [];
  // Monotonic id counter for stable React keys; not tied to timestamp because
  // two entries logged in the same millisecond must still be distinguishable.
  private _consoleLogIdCounter = 0;

  // The authenticated user's email, or undefined when auth is off.
  // Used as the user identity in request contexts for logging. When
  // undefined, the IDE context helpers fall back to the OS username.
  get userId(): string | undefined {
    return this.client.userEmail ?? undefined;
  }

  constructor(readonly client: AlphaClient) {
    this.commandCenter = new CommandCenter(
      (open) => {
        this._commandPaletteOpen = open;
        this.updateSnapshot();
      },
      () => this.getActiveEditor()?.handleKey ?? null
    );
    registerBuiltinCommands(this.commandCenter, this);

    // Properties Panel controller. The host adapter bridges the controller to
    // the Workspace internals it needs (active editor / design, dirty-marking,
    // instance create/update, content-version bump, snapshot publish) without
    // exposing them more widely.
    const propertiesHost: PropertiesHost = {
      client: this.client,
      getUserId: () => this.userId,
      getActiveEditor: () => this.getActiveEditor(),
      getActiveIdent: () => this.activeIdent,
      markDirty: (ident) => {
        this.markDirty(ident);
      },
      createInstance: (params) => this.createInstance(params),
      updateInstance: (params) => this.updateInstance(params),
      bumpContentVersion: () => {
        this._contentVersion++;
      },
      publishSnapshot: () => {
        this.updateSnapshot();
      },
    };
    this.properties = new PropertiesController(propertiesHost);

    // Construct the SRV model with this workspace's dependencies. The injected
    // closures resolve the active schematic (as a data-model `Schematic` plus
    // its paired symbol view for Pin Matching), reveal off-screen violations,
    // surface toasts, and notify React of state changes. `onChange` will additionally
    // push the overlay state into the active schematic content once the canvas
    // arrow overlay lands (feature 4d).
    this.srv = new SrvModel({
      // Fetch the read-only rule descriptor list from the Rust engine (via
      // AlphaServer), so the panel's reference list always matches ALL_RULES.
      listSrvRules: (ctx) => this.requestSrvRules(ctx),
      makeContext: () => createRequestContext(this.userId),
      // Fire the run-srv request; the rules run on the Rust tool server and the
      // result is pushed back over the WebSocket (applied via applySrvResult).
      // Server-side resolution reads the saved/live schematic state and resolves
      // symbols (including the paired symbol view) from the depot.
      requestSrv: (ctx) => this.requestSrv(ctx),
      // Check & Save persists the active design before running the checks, so the
      // feature name isn't a bait-and-switch and the shortcut reliably saves.
      save: () => this.save(),
      // Cheap gate for the Run button: true when the active diagram is a
      // schematic. Avoids materializing the schematic data on every render.
      isSchematicActive: () =>
        (this.getActiveEditor()?.getSchematicContent() ?? null) !== null,
      revealBbox: (bbox) => {
        this.getActiveEditor()?.viewport.centerOnBbox(bbox);
      },
      showToast: (msg) => {
        this.setStatusMessage({ text: msg, kind: "error" }, 5000);
      },
      watchActiveDiagram: (observer) => this.subscribeActiveDiagram(observer),
      onChange: () => {
        // Push the overlay to the active schematic for the canvas arrows, then
        // re-render React. The overlay is non-null only while SRV is active
        // (results present, panel visible, highlights enabled).
        const editor = this.getActiveEditor();
        const state = this.srv.getState();
        if (this.srv.isActive() && state.lastRun !== null) {
          editor?.setSrvOverlay({
            run: state.lastRun,
            highlightedViolationIndex: state.highlightedViolationIndex,
            onArrowClick: (index: number) => {
              this.srv.selectViolation(index);
            },
          });
        } else {
          editor?.clearSrvOverlay();
        }
        this.updateSnapshot();
      },
    });

    // Subscribe to theme / grid-intensity changes so that every open
    // editor's viewport matches the resolved light/dark mode and intensity.
    // The initial theme is applied at UserPreferencesStore construction
    // time, well before any editor exists.
    userPreferences.subscribe(() => {
      const resolved = userPreferences.resolvedTheme();
      const { gridIntensity, trackpadMode } = userPreferences.get();
      for (const entry of this.editors.values()) {
        entry.editor.setTheme(
          resolved === IdeThemeMode.Dark ? Theme.Dark : Theme.Light
        );
        entry.editor.grid.setHighIntensity(
          gridIntensity === GridIntensity.High
        );
        entry.editor.trackpadMode = trackpadMode;
      }
    });

    // Prompt the user before closing or reloading the tab when there are
    // unsaved changes or an AI prompt is in flight.
    window.addEventListener("beforeunload", (event) => {
      if (this.dirtyIdents.size > 0 || this._isPromptInFlight) {
        event.preventDefault();
      }
    });
  }

  // Store the dockview API for later use (adding/removing editor panels).
  // sidebarGroupIds maps positional names ("left", "right", "bottom") to the
  // dockview group IDs that were created in those positions at layout init.
  setDockviewApi(
    api: DockviewApi,
    sidebarGroupIds: Record<"left" | "right" | "bottom", string>
  ): void {
    this.dockApi = api;
    this._sidebarGroupIds = sidebarGroupIds;
    this.setupDockviewListeners(api);
  }

  // Sidebar panel group visibility state.
  private _leftPanelVisible = true;
  private _rightPanelVisible = true;
  private _bottomPanelVisible = true;

  // Maps positional names to dockview group IDs, set at layout init.
  private _sidebarGroupIds: Record<"left" | "right" | "bottom", string> | null =
    null;

  // Toggle visibility of a sidebar panel group by its position (left, right,
  // or bottom). Uses the group IDs captured at layout init so the toggle
  // always targets the positional group, regardless of which panels are in it.
  toggleSidebarGroup(side: "left" | "right" | "bottom"): void {
    if (!this.dockApi || !this._sidebarGroupIds) return;
    const groupId = this._sidebarGroupIds[side];
    const group = this.dockApi.getGroup(groupId);
    if (!group) return;

    const field = `_${side}PanelVisible` as const;
    const nowVisible = !this[field];
    this[field] = nowVisible;
    group.api.setVisible(nowVisible);
    this.updateSnapshot();
  }

  // Show and activate a specific panel by its ID (e.g., "help", "lvs").
  // Ensures the panel's group is visible if it's in a sidebar.
  showPanel(panelId: string): void {
    if (!this.dockApi) return;
    const panel = this.dockApi.getPanel(panelId);
    if (!panel) return;

    // Ensure the panel's group is visible
    const groupId = panel.group?.id;
    if (groupId && this._sidebarGroupIds) {
      if (
        groupId === this._sidebarGroupIds.bottom &&
        !this._bottomPanelVisible
      ) {
        this._bottomPanelVisible = true;
        const group = this.dockApi.getGroup(groupId);
        group?.api.setVisible(true);
      } else if (
        groupId === this._sidebarGroupIds.left &&
        !this._leftPanelVisible
      ) {
        this._leftPanelVisible = true;
        const group = this.dockApi.getGroup(groupId);
        group?.api.setVisible(true);
      } else if (
        groupId === this._sidebarGroupIds.right &&
        !this._rightPanelVisible
      ) {
        this._rightPanelVisible = true;
        const group = this.dockApi.getGroup(groupId);
        group?.api.setVisible(true);
      }
    }

    // Activate the panel
    panel.api.setActive();
    this.updateSnapshot();
  }

  // Collapse a panel to a narrow tab. The panel content is hidden but the tab
  // remains visible and can be clicked to expand.
  collapsePanel(panelId: string): void {
    this._collapsedPanels.add(panelId);
    this.updateSnapshot();
  }

  // Expand a previously collapsed panel, restoring its full tab and content.
  expandPanel(panelId: string): void {
    this._collapsedPanels.delete(panelId);
    this.updateSnapshot();
  }

  // Check if a panel is currently collapsed.
  isPanelCollapsed(panelId: string): boolean {
    return this._collapsedPanels.has(panelId);
  }

  // Focus the Layers panel in the left sidebar. Ensures the left group
  // is visible and activates the "layers" tab within it.
  focusLayerPanel(): void {
    if (!this.dockApi) return;
    // Ensure the left panel group is visible
    if (!this._leftPanelVisible) {
      this.toggleSidebarGroup("left");
    }
    const panel = this.dockApi.getPanel("layers");
    if (panel) {
      panel.api.setActive();
    }
  }

  // Focus the Keyboard Help panel in the right sidebar. Ensures the right
  // group is visible and activates the "keyboard-help" tab within it.
  focusKeyboardHelpPanel(): void {
    if (!this.dockApi) return;
    // Ensure the right panel group is visible
    if (!this._rightPanelVisible) {
      this.toggleSidebarGroup("right");
    }
    const panel = this.dockApi.getPanel("keyboard-help");
    if (panel) {
      panel.api.setActive();
    }
  }

  // Show all layers on the active layout editor and bump the layer panel
  // visibility version so the React component re-renders.
  showAllLayers(): void {
    const editor = this.getActiveEditor();
    if (!editor || editor.content.contentKind !== "layout") return;
    // LayoutContent.showAllLayers() clears hiddenLayers and redraws
    (editor.content as { showAllLayers: () => void }).showAllLayers();
    // Bump contentVersion to trigger layer panel re-render
    this._contentVersion++;
    this.updateSnapshot();
  }

  // Toggle a display option on the active layout editor
  // and bump the content version so the LayerPanel re-renders.
  toggleDisplayOption(
    option:
      | "pins"
      | "pinLabels"
      | "instanceLabels"
      | "routeLabels"
      | "abstractView"
      | "blockages"
  ): void {
    const editor = this.getActiveEditor();
    if (!editor) return;
    // Abstract view lives on Editor, not LayoutContent
    if (option === "abstractView") {
      editor.toggleAbstractView();
      this._contentVersion++;
      this.updateSnapshot();
      return;
    }
    const lc = editor.getLayoutContent();
    if (!lc) return;
    lc.toggleLayoutDisplay(option);
    this._contentVersion++;
    this.updateSnapshot();
  }

  // Whether an LLM prompt is currently in flight (viewports locked).
  private _isPromptInFlight = false;

  // Request ID for the current in-flight prompt (used for cancellation).
  private _currentRequestId: string | null = null;

  // Ident to reveal in the Depot tree (set by context menu "View in Depot").
  private _revealInDepotIdent: schema.Ident | null = null;

  // Instance to reveal in the Hierarchy panel (set by context menu "Show in Hierarchy").
  private _hierarchyRevealTarget: {
    name: string;
    id: number;
  } | null = null;

  // Parent breadcrumbs: maps a child design (stringified ident) to the parent
  // design it was opened from. Used by "Open Parent" in the background context menu.
  // Ephemeral — not persisted across sessions. Last-opener-wins.
  private readonly _parentBreadcrumbs = new Map<string, schema.Ident>();

  // State for the instance dialog (add new or edit existing).
  private _instanceDialog: InstanceDialogState | null = null;

  // Info about the active design (content kind and PDK), used for depot filtering.
  private _activeDesignInfo: ActiveDesignInfo | null = null;

  // State for the schematic pin dialog (new / editProperties / rename).
  private _pinDialog: PinDialogState | null = null;

  // State for the "Edit Wire Label" dialog.
  private _editWireLabelDialog: EditWireLabelDialogState | null = null;

  // State for the "Set Route Width" dialog.
  private _editRouteWidthDialog: EditRouteWidthDialogState | null = null;

  // State for the "Recover unsaved session" dialog.
  private _recoverSessionDialog: RecoverSessionDialogState | null = null;

  // State for the "Add/Edit Text" annotation dialog.
  private _textAnnotationDialog: TextAnnotationDialogState | null = null;

  // State for the "Manage Linkages" dialog.
  private _manageLinkagesDialog: ManageLinkagesDialogState | null = null;

  // State for the constraint dialog (create/edit).
  private _constraintDialog: ConstraintDialogState | null = null;

  // State for the "Add Dummy Devices" dialog.
  private _dummyDeviceDialog: DummyDeviceDialogState | null = null;

  // State for the "Configure Power Routing" dialog.
  private _powerRoutingDialog: PowerRoutingDialogState | null = null;

  // Set of panel IDs that are currently collapsed (showing narrow tab only).
  private readonly _collapsedPanels = new Set<string>();

  // Cancel the current in-flight prompt request.
  // Aborts the HTTP fetch and notifies the server to stop processing.
  cancelPrompt(): void {
    if (!this._isPromptInFlight || !this._currentRequestId) {
      return;
    }
    const ctx = createRequestContext(this.userId);
    void this.client.cancelPrompt(ctx, this._currentRequestId);
  }

  // Lock all open editor viewports. Called when an LLM prompt starts.
  private lockAllEditors(): void {
    for (const entry of this.editors.values()) {
      entry.editor.isLocked = true;
      entry.editor.parentDomElement.style.cursor = "not-allowed";
      entry.editor.overlays.showLockOverlay();
    }
    this._isPromptInFlight = true;
    this.updateSnapshot();
  }

  // Unlock all open editor viewports. Called when an LLM prompt completes.
  private unlockAllEditors(): void {
    for (const entry of this.editors.values()) {
      entry.editor.isLocked = false;
      entry.editor.parentDomElement.style.cursor = "";
      entry.editor.overlays.hideLockOverlay();
    }
    this._isPromptInFlight = false;
    this.updateSnapshot();
  }

  // Wire up dockview events so workspace state stays in sync when the user
  // drags/closes/activates panels via the dockview UI.
  // Note: This method is only called once during workspace initialization.
  // Single chokepoint for changing the active diagram. Every path that switches
  // editors routes through here so observers (observer pattern) are notified on
  // an actual change. Observers — e.g. SrvModel clearing stale results — react
  // without the switching code needing to know about them.
  private setActiveIdent(ident: Ident | null): void {
    const prev = this.activeIdent;
    const unchanged =
      prev === ident ||
      (prev !== null &&
        ident !== null &&
        Ident.stringify(prev) === Ident.stringify(ident));
    this.activeIdent = ident;
    if (unchanged) return;
    // Clear any stale selection from the previously-active design so a
    // background editor's selection never leaks into the Properties Panel.
    this.properties.clearSelection();
    for (const observer of this.activeDiagramObservers) {
      observer.onActiveDiagramChanged(ident);
    }
  }

  // ActiveDiagramSubject: register for active-diagram-change notifications.
  // Returns an unsubscribe function.
  subscribeActiveDiagram(observer: ActiveDiagramObserver): () => void {
    this.activeDiagramObservers.add(observer);
    return () => this.activeDiagramObservers.delete(observer);
  }

  private setupDockviewListeners(api: DockviewApi): void {
    // Sync active panel -> workspace activeIdent
    api.onDidActivePanelChange((panel: IDockviewPanel | undefined) => {
      if (!panel) return;

      // Clear LVS highlights when switching to another tab in the bottom panel group.
      // Keep highlights when clicking on the main editor or other panels.
      if (panel.id !== "lvs") {
        const lvsPanel = api.getPanel("lvs");
        if (lvsPanel && panel.group === lvsPanel.group) {
          this.highlightLvsError(null);
        }
      }

      // Only handle editor panels (not sidebar/bottom)
      const entry = this.findEditorByKey(panel.id);
      if (
        entry &&
        (!this.activeIdent || Ident.stringify(this.activeIdent) !== panel.id)
      ) {
        this.setActiveIdent(entry.ident);
        this.updateSnapshot();
        this.fetchActiveDesignInfo();
      }
    });

    // Handle panel closed via dockview UI (close button, drag out)
    api.onDidRemovePanel((panel: IDockviewPanel) => {
      // Clear LVS highlights when LVS panel is closed
      if (panel.id === "lvs") {
        this.highlightLvsError(null);
      }
      if (this._closingFromWorkspace) return; // we initiated this close
      const entry = this.findEditorByKey(panel.id);
      if (entry) {
        this.cleanupClosedTab(entry.ident, panel.id);
      }
    });
  }

  // Subscribe to state changes. Returns an unsubscribe function.
  // Designed for React's useSyncExternalStore.
  subscribe = (listener: SnapshotListener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  // Get the current immutable snapshot of workspace state.
  getSnapshot = (): WorkspaceSnapshot => {
    return this.snapshot;
  };

  // Open an editor for the given Ident.
  // If already open, activates the existing tab instead.
  // Creates the Editor instance but does not add the dockview panel yet.
  // Call addDockviewPanel() separately after loading content.
  open(ident: Ident): void {
    if (this.editors.has(ident)) {
      this.activate(ident);
      return;
    }

    // Create a container div for this editor's canvas
    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.position = "relative";

    // Create a WorkspacePlatform that routes messages through us
    const platform = new WorkspacePlatform(this, ident);

    // Create the Editor.
    // events: true to register message handler and set up UI events.
    const editor = new Editor(platform, container, { events: true });

    // If an LLM prompt is in flight, lock this editor immediately so it
    // matches the state of all other open editors.
    if (this._isPromptInFlight) {
      editor.isLocked = true;
      editor.parentDomElement.style.cursor = "not-allowed";
      editor.overlays.showLockOverlay();
    }

    // Apply the current IDE theme, grid intensity, and trackpad mode to
    // the new editor so it matches the rest of the workspace.
    const resolved = userPreferences.resolvedTheme();
    const prefs = userPreferences.get();
    editor.setTheme(resolved === IdeThemeMode.Dark ? Theme.Dark : Theme.Light);
    editor.grid.setHighIntensity(prefs.gridIntensity === GridIntensity.High);
    editor.trackpadMode = prefs.trackpadMode;

    // Set up drag-and-drop from depot tree onto the canvas.
    this.setupDepotDropTarget(container, editor, ident);

    const entry: EditorEntry = {
      ident,
      editor,
      platform,
      container,
    };
    this.editors.set(entry);
    this.tabOrder.push(ident);

    // Activate the newly opened editor. This is always a different diagram than
    // whatever was active, so observers (e.g. SRV) are notified of the switch.
    this.setActiveIdent(ident);
    this.updateSnapshot();
    this.fetchActiveDesignInfo();

    logger.log(`[Workspace] Opened editor for ${Ident.displayName(ident)}`);
  }

  // Add a dockview panel for an already-created editor.
  // Called after content is loaded so that when React mounts EditorTabPanel,
  // the content is ready and fit() can compute correct viewport dimensions.
  private addDockviewPanel(ident: Ident): void {
    const key = Ident.stringify(ident);
    if (!this.dockApi) return;

    // Don't add a duplicate panel if one already exists (e.g. re-opening a design).
    if (this.dockApi.getPanel(key)) return;

    const editorGroup = this.findEditorGroup();
    if (editorGroup) {
      // Place in the existing editor group (as a new tab)
      this.dockApi.addPanel({
        id: key,
        component: "editor-tab",
        tabComponent: "editor-tab",
        title: Ident.displayName(ident),
        position: { referenceGroup: editorGroup },
      });
    } else {
      // First editor panel — no reference needed, dockview places it
      this.dockApi.addPanel({
        id: key,
        component: "editor-tab",
        tabComponent: "editor-tab",
        title: Ident.displayName(ident),
      });
    }
  }

  // Activate (switch to) an existing editor tab.
  // Also focuses the corresponding dockview panel.
  activate(ident: Ident): void {
    if (!this.editors.has(ident)) return;
    // Routes through setActiveIdent, which notifies observers only when this is
    // an actual switch (re-activating the already-active tab is a no-op).
    this.setActiveIdent(ident);

    // Focus the dockview panel so it visually selects
    const key = Ident.stringify(ident);
    const panel = this.dockApi?.getPanel(key);
    if (panel) {
      panel.api.setActive();
      // NOTE: Dockview's built-in scroll logic only aligns the tab's left edge,
      // which can leave the tab partially clipped. After the DOM updates,
      // scroll the active tab fully into view.
      // NOTE: Dockview's GroupPanel exposes an `element` property that isn't in the
      // public type definitions, but we need to access it to correct the behavior.
      requestAnimationFrame(() => {
        const groupEl = (panel.group as { element?: HTMLElement }).element;
        if (!groupEl) return;
        const activeTab = groupEl.querySelector(".dv-active-tab");
        if (activeTab) {
          activeTab.scrollIntoView({ inline: "nearest", block: "nearest" });
        }
      });
    }

    this.updateSnapshot();
    this.fetchActiveDesignInfo();
  }

  // Close an editor tab and dispose of its resources.
  // Also removes the corresponding dockview panel.
  close(ident: Ident): void {
    const entry = this.editors.get(ident);
    if (!entry) return;

    const key = Ident.stringify(ident);

    // Remove the dockview panel. This triggers onDidRemovePanel, so set the
    // re-entry guard so the listener doesn't double-clean.
    this._closingFromWorkspace = true;
    try {
      const panel = this.dockApi?.getPanel(key);
      if (panel) {
        panel.api.close();
      }
    } finally {
      this._closingFromWorkspace = false;
    }

    this.cleanupClosedTab(ident, key);
  }

  // Shared cleanup for a closed tab. Called from both close() (programmatic)
  // and onDidRemovePanel (dockview UI close).
  private cleanupClosedTab(ident: Ident, key: string): void {
    this.editors.delete(ident);
    const idx = this.tabOrder.findIndex((i) => Ident.stringify(i) === key);
    if (idx >= 0) {
      this.tabOrder.splice(idx, 1);
    }

    // If we closed the active tab, activate an adjacent one (or null when the
    // last tab closes). Routes through setActiveIdent so observers are notified.
    if (this.activeIdent && Ident.stringify(this.activeIdent) === key) {
      if (this.tabOrder.length > 0) {
        const nextIdx = Math.min(idx, this.tabOrder.length - 1);
        this.setActiveIdent(this.tabOrder[nextIdx]);
      } else {
        this.setActiveIdent(null);
      }
    }

    // Discard dirty state and breadcrumb for the closed design
    this.dirtyIdents.delete(key);
    this._parentBreadcrumbs.delete(key);
    if (this.dirtyIdents.size === 0 && this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    this.updateSnapshot();
    this.fetchActiveDesignInfo();

    logger.log(`[Workspace] Closed editor for ${Ident.displayName(ident)}`);
  }

  // Track an in-flight "changes" HTTP request so save() can wait for it.
  trackPendingChange(promise: Promise<unknown>): void {
    this._pendingChanges.add(promise);
    void promise.finally(() => this._pendingChanges.delete(promise));
  }

  // Mark a design as having unsaved changes. Called by WorkspacePlatform
  // on each GUI edit. Resets the auto-save timer.
  markDirty(ident: Ident): void {
    const key = Ident.stringify(ident);
    this.dirtyIdents.add(key);
    this.resetAutoSaveTimer();

    // Bump the hierarchy version on every edit so the Object Hierarchy panel
    // rebuilds its top-level entries as instances are added / renamed / deleted.
    // This is the single chokepoint all edit paths share (interactive canvas
    // edits via notifyUIChange, create-instance / streamed / collaborative edits
    // via applyServerChangesLocally, and rename/param edits via updateInstance),
    // so a bump here keeps the tree live without polling. It stays separate from
    // contentVersion, which drives network re-fetches in the LVS/DRC/IVA/
    // constraints panels — those must not churn on every edit.
    this._hierarchyVersion++;
    this.updateSnapshot();
  }

  // Get the container div for the active editor.
  // Used by React's EditorPane to attach the canvas to the DOM.
  getActiveContainer(): HTMLDivElement | null {
    if (!this.activeIdent) return null;
    const entry = this.editors.get(this.activeIdent);
    return entry?.container ?? null;
  }

  // Get the active Editor instance.
  // Get the center of the active editor's viewport in world coordinates.
  // Returns { x: 0, y: 0 } if no active editor.
  getViewportCenter(): { x: number; y: number } {
    const editor = this.getActiveEditor();
    if (!editor) return { x: 0, y: 0 };
    const { width, height } = editor.canvas.size;
    const center = editor.canvas.worldTransforms.toSch({
      x: width / 2,
      y: height / 2,
    });
    return { x: center.x, y: center.y };
  }

  getActiveEditor(): Editor | null {
    if (!this.activeIdent) return null;
    const entry = this.editors.get(this.activeIdent);
    return entry?.editor ?? null;
  }

  // Properties Panel selection / descriptor / edit logic lives on the public
  // `properties` controller — call `workspace.properties.*` directly.

  // Get the current LVS verification results.
  // Returns null if no LVS has been run.
  getLvsResult(): LvsResult | null {
    return this._lvsResult;
  }

  // Set LVS verification results.
  // Called by the run-lvs tool when LVS completes.
  // Automatically switches to the LVS panel when results arrive.
  setLvsResult(result: LvsResult | null): void {
    this._lvsResult = result;
    this._contentVersion++;
    this.updateSnapshot();

    // Switch to the LVS panel when results arrive
    if (result !== null) {
      const lvsPanel = this.dockApi?.getPanel("lvs");
      if (lvsPanel) {
        lvsPanel.api.setActive();
      }
    }
  }

  // Run LVS verification on the active layout.
  // Returns a Result indicating success or failure.
  // The LVS results are broadcast via WebSocket and stored in workspace state.
  // Errors are displayed via the status bar.
  async runLvs(
    mode: "name_based" | "topology_based"
  ): Promise<Result<void, { why: string }>> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err({ why: "no-active-editor" });
    }
    if (editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "run-lvs",
      data: {
        designIdent: this.activeIdent,
        mode,
      },
    });

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      this.setStatusMessage(
        { text: `LVS failed: ${errorMsg}`, kind: "error" },
        5000
      );
      return Result.Err({ why: errorMsg });
    }

    return Result.Ok(undefined);
  }

  // Fire a run-srv request for the active schematic. The SRV rules run on the
  // Rust tool server; the result is pushed back over the WebSocket and applied
  // via SrvModel.applySrvResult. Returns a Result reporting request-level
  // failures only (the violations arrive asynchronously).
  async requestSrv(ctx: Context): Promise<Result<void, { why: string }>> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err({ why: "no-active-editor" });
    }
    if (editor.content.contentKind !== "schematic") {
      return Result.Err({ why: "not-a-schematic" });
    }
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const result = await this.client.request(ctx, {
      kind: "run-srv",
      data: { designIdent: this.activeIdent },
    });

    if (result.err) {
      return Result.Err({ why: extractErrorMessage(result.val) });
    }

    return Result.Ok(undefined);
  }

  // Fetch the read-only SRV rule descriptor list from the backend (Rust engine
  // via AlphaServer). The list is sourced from the ALL_RULES registry, so it
  // always matches the rules the engine runs. Returns a Result the SrvModel uses
  // to populate the panel's "Rules that will run" reference list.
  async requestSrvRules(
    ctx: Context
  ): Promise<Result<Array<SrvRuleDescriptor>, { why: string }>> {
    const result = await this.client.request(ctx, {
      kind: "list-srv-rules",
      data: {},
    });
    if (result.err) {
      return Result.Err({ why: extractErrorMessage(result.val) });
    }
    const validated = ListSrvRulesResult.validator.validate(result.val);
    if (validated.err) {
      return Result.Err({ why: "invalid-srv-rule-list" });
    }
    return Result.Ok(validated.val);
  }

  // Get the current DRC verification results.
  // Returns null if no DRC has been run.
  getDrcResult(): DrcResult | null {
    return this._drcResult;
  }

  // Set DRC verification results.
  // Called by the run-drc tool when DRC completes.
  // Automatically switches to the DRC panel when results arrive.
  setDrcResult(result: DrcResult | null): void {
    this._drcResult = result;
    this._contentVersion++;
    this.updateSnapshot();

    // Switch to the DRC panel when results arrive
    if (result !== null) {
      const drcPanel = this.dockApi?.getPanel("drc");
      if (drcPanel) {
        drcPanel.api.setActive();
      }
    }
  }

  // Run DRC verification on the active layout.
  // Returns a Result indicating success or failure.
  // The DRC results are broadcast via WebSocket and stored in workspace state.
  // Errors are displayed via the status bar.
  async runDrc(): Promise<Result<void, { why: string }>> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err({ why: "no-active-editor" });
    }
    if (editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "run-drc",
      data: { designIdent: this.activeIdent },
    });

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      this.setStatusMessage(
        { text: `DRC failed: ${errorMsg}`, kind: "error" },
        5000
      );
      return Result.Err({ why: errorMsg });
    }

    return Result.Ok(undefined);
  }

  // Get the current IVA verification results.
  // Returns null if no IVA has been run.
  getIvaResult(): IvaResult | null {
    return this._ivaResult;
  }

  // Set IVA verification results.
  // Called by the run-iva tool when IVA completes.
  // Automatically switches to the IVA panel when results arrive.
  setIvaResult(result: IvaResult | null): void {
    this._ivaResult = result;
    this._contentVersion++;
    this.updateSnapshot();

    if (result !== null) {
      const ivaPanel = this.dockApi?.getPanel("iva");
      if (ivaPanel) {
        ivaPanel.api.setActive();
      }
    }
  }

  // Run IVA (Implementation vs Abstract) verification on the active layout.
  // Single-cell A/B/C checks; recursive bottom-up is a follow-up.
  async runIva(): Promise<Result<void, { why: string }>> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err({ why: "no-active-editor" });
    }
    if (editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "run-iva",
      data: { designIdent: this.activeIdent },
    });

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      this.setStatusMessage(
        { text: `IVA failed: ${errorMsg}`, kind: "error" },
        5000
      );
      return Result.Err({ why: errorMsg });
    }

    return Result.Ok(undefined);
  }

  // Infer symmetry constraints from the active schematic or linked schematic.
  // For schematics: infers constraints directly.
  // For layouts: infers constraints for the linked schematic.
  // Saves constraints to schematic state and triggers UI refresh.
  async inferConstraints(): Promise<Result<void, { why: string }>> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err({ why: "no-active-editor" });
    }

    // Get the schematic ident (either active schematic or linked schematic for layouts)
    const schematicIdent = this.getSchematicIdentForConstraints();
    if (!schematicIdent) {
      return Result.Err({ why: "no-schematic-for-constraints" });
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "infer-constraints",
      data: { designIdent: schematicIdent },
    });

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      this.setStatusMessage(
        { text: `Constraint inference failed: ${errorMsg}`, kind: "error" },
        5000
      );
      return Result.Err({ why: errorMsg });
    }

    // Show success message with constraint count
    const constraintCount =
      typeof result.val === "object" &&
      result.val &&
      "constraintCount" in result.val
        ? result.val.constraintCount
        : 0;
    this._constraintsVersion++;
    this.updateSnapshot();
    this.setStatusMessage(
      { text: `Inferred ${constraintCount} constraints`, kind: "info" },
      3000
    );

    return Result.Ok(undefined);
  }

  // Open the constraint dialog for creating a new constraint.
  openCreateConstraintDialog(): void {
    if (!this.activeIdent) return;
    this._constraintDialog = {
      designIdent: this.activeIdent,
      mode: "create",
      constraint: {
        // id is undefined for create mode - assigned by server
        description: "",
        constraintType: "",
        firstInstances: [],
        secondInstances: [],
      },
    };
    this.updateSnapshot();
  }

  // Open the constraint dialog for editing an existing constraint.
  openEditConstraintDialog(
    constraintId: number,
    description: string,
    constraintType: string,
    firstInstances: string[],
    secondInstances: string[],
    groupId?: number
  ): void {
    if (!this.activeIdent) return;
    this._constraintDialog = {
      designIdent: this.activeIdent,
      mode: "edit",
      constraint: {
        id: constraintId,
        description,
        constraintType,
        firstInstances,
        secondInstances,
      },
      groupId,
    };
    this.updateSnapshot();
  }

  // Close the constraint dialog.
  closeConstraintDialog(): void {
    this._constraintDialog = null;
    this.updateSnapshot();
  }

  // Open the "Add Dummy Devices" dialog.
  openDummyDeviceDialog(
    designIdent: Ident,
    instanceIds: number[],
    instanceNames: string[]
  ): void {
    this._dummyDeviceDialog = {
      designIdent,
      instanceIds,
      instanceNames,
      nrows: 1,
      ncols: 1,
      error: null,
    };
    this.updateSnapshot();
  }

  // Close the "Add Dummy Devices" dialog.
  closeDummyDeviceDialog(): void {
    this._dummyDeviceDialog = null;
    this.updateSnapshot();
  }

  // Submit the "Add Dummy Devices" request to AlphaServer.
  async submitDummyDevices(params: {
    designIdent: Ident;
    instanceIds: number[];
    nrows: number;
    ncols: number;
    dummyName: string;
  }): Promise<Result<unknown>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "add-dummy-devices",
      data: {
        designIdent: params.designIdent,
        instanceIds: params.instanceIds,
        nrows: params.nrows,
        ncols: params.ncols,
        dummyName: params.dummyName,
      },
    });
    if (result.ok) {
      this.markDirty(params.designIdent);
    }
    return result;
  }

  // Get the schematic ident for constraint operations.
  // If viewing a schematic, returns its ident directly.
  // If viewing a layout, returns the linked schematicImpl ident.
  private getSchematicIdentForConstraints(): schema.Ident | null {
    const editor = this.getActiveEditor();
    if (!editor) return null;

    const content = editor.content;
    if (content.contentKind === "schematic") {
      return this.activeIdent;
    }
    if (content.contentKind === "layout") {
      // Get schematicImpl directly from LayoutContent - don't go through
      // getMainContent() as it loses schematicImpl during serialization
      const layoutContent = content as { schematicImpl?: schema.Ident };
      const schematicImpl = layoutContent.schematicImpl;
      // Basic sanity check that it looks like an Ident (has "kind" field)
      if (
        schematicImpl &&
        typeof schematicImpl === "object" &&
        "kind" in schematicImpl
      ) {
        return schematicImpl;
      }
      return null;
    }
    return null;
  }

  // Helper for constraint operations via the changes API.
  // Applies change locally to EditorCore first (for instant UI), then sends to server.
  // Derives success/error messages from the change's changeKind.
  private async sendConstraintChange(
    change: SchematicChange
  ): Promise<Result<void, { why: string }>> {
    const schematicIdent = this.getSchematicIdentForConstraints();
    if (!schematicIdent) {
      return Result.Err({ why: "no-schematic-for-constraints" });
    }

    // Apply change locally to EditorCore first for instant UI feedback.
    // This mirrors how other GUI changes work (EditorCore applies first, then notifies server).
    const editor = this.getEditorForKey(Ident.stringify(schematicIdent));
    if (editor) {
      await editor.content.applyChangeRequest([change]);
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "changes",
      data: {
        changes: [change],
        source: "gui",
        ident: schematicIdent,
      },
    });

    // Use changeKind directly in messages: "add" -> "added", "update" -> "updated", etc.
    const pastTense =
      change.changeKind === "add" ? "added" : `${change.changeKind}d`;

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      this.setStatusMessage(
        {
          text: `Failed to ${change.changeKind} constraint: ${errorMsg}`,
          kind: "error",
        },
        5000
      );
      return Result.Err({ why: errorMsg });
    }

    this._contentVersion++;
    this._constraintsVersion++;
    this.updateSnapshot();
    this.setStatusMessage(
      { text: `Constraint ${pastTense}`, kind: "info" },
      2000
    );

    // Mark the schematic as dirty so the tab shows the asterisk.
    // This works for both direct schematic edits and layout-initiated edits
    // (where schematicIdent is the linked schematic).
    this.markDirty(schematicIdent);

    // No manual reload needed - EditorCore applies constraint changes locally
    // (same as other GUI changes like instances/pins/wires).

    return Result.Ok(undefined);
  }

  // Add a new constraint to the active schematic via changes API.
  async addConstraint(constraint: {
    description?: string;
    constraintType: string;
    firstInstances: string[];
    secondInstances: string[];
    groupId?: number;
  }): Promise<Result<void, { why: string }>> {
    // Generate ID client-side so both EditorCore and server use the same ID.
    // Server's IdMap.add() preserves existing IDs.
    const id = randomId();
    const change = SchematicChange.add({
      targetKind: "constraint",
      id,
      description: constraint.description,
      constraintType: constraint.constraintType,
      firstInstances: constraint.firstInstances,
      secondInstances: constraint.secondInstances,
      groupId: constraint.groupId,
    });
    return this.sendConstraintChange(change);
  }

  // Update a constraint with new values via changes API.
  // Constraints use numeric IDs like other entities.
  async updateConstraint(
    constraintId: number,
    updates: {
      description?: string;
      constraintType?: string;
      firstInstances?: string[];
      secondInstances?: string[];
      groupId?: number;
    }
  ): Promise<Result<void, { why: string }>> {
    const change = SchematicChange.update(constraintId, {
      targetKind: "constraint",
      ...updates,
    });
    return this.sendConstraintChange(change);
  }

  // Remove a constraint by ID from the active schematic via changes API.
  // Constraints use numeric IDs like other entities.
  async removeConstraint(
    constraintId: number
  ): Promise<Result<void, { why: string }>> {
    const change = SchematicChange.remove("constraint", constraintId);
    return this.sendConstraintChange(change);
  }

  // Add a new constraint group via changes API. Returns the client-generated group ID.
  async addConstraintGroup(group: {
    description?: string;
    symmetryDirection?: "vertical" | "horizontal";
  }): Promise<Result<number, { why: string }>> {
    const id = randomId();
    const change = SchematicChange.add({
      targetKind: "constraint_group",
      id,
      description: group.description,
      symmetryDirection: group.symmetryDirection ?? "vertical",
    });
    const result = await this.sendConstraintChange(change);
    if (result.err) return result;
    return Result.Ok(id);
  }

  // Remove a constraint group and all its constraints via changes API.
  async removeConstraintGroup(
    groupId: number
  ): Promise<Result<void, { why: string }>> {
    const change = SchematicChange.remove("constraint_group", groupId);
    return this.sendConstraintChange(change);
  }

  // Toggle the symmetry direction of a constraint group between vertical and horizontal.
  async toggleGroupSymmetryDirection(
    groupId: number,
    currentDirection: "vertical" | "horizontal"
  ): Promise<Result<void, { why: string }>> {
    const newDirection =
      currentDirection === "vertical" ? "horizontal" : "vertical";
    const change = SchematicChange.update(groupId, {
      targetKind: "constraint_group",
      symmetryDirection: newDirection,
    });
    return this.sendConstraintChange(change);
  }

  // Place all constraints symmetrically on the layout.
  // Only available when viewing a layout with a linked schematic.
  async placeAllConstraints(): Promise<Result<void, { why: string }>> {
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const editor = this.getActiveEditor();
    if (!editor || editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }

    this.setStatusMessage(
      { text: "Placing instances with symmetry...", kind: "info" },
      0 // Keep showing until we get a result
    );

    const ctx = createRequestContext();
    const result = await this.client.request(ctx, {
      kind: "place-with-symmetry",
      data: { designIdent: this.activeIdent },
    });

    if (result.err) {
      this.setStatusMessage(
        {
          text: `Place failed: ${extractErrorMessage(result.val)}`,
          kind: "error",
        },
        5000
      );
      return Result.Err({ why: result.val.why });
    }

    this.setStatusMessage({ text: "Placement complete", kind: "info" }, 3000);
    return Result.Ok(undefined);
  }

  // Place a single constraint symmetrically on the layout.
  // Only available when viewing a layout.
  async placeConstraint(
    constraintId: number
  ): Promise<Result<void, { why: string }>> {
    // Select just this constraint and place it
    this._selectedConstraintMatchIds.clear();
    this._selectedConstraintMatchIds.add(constraintId);
    this.updateSnapshot();
    return this.placeSelectedConstraints();
  }

  // Place the currently selected constraint matches.
  // Sends selected match IDs to AlphaServer so only those constraints are placed.
  async placeSelectedConstraints(): Promise<Result<void, { why: string }>> {
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const editor = this.getActiveEditor();
    if (!editor || editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }

    if (this._selectedConstraintMatchIds.size === 0) {
      return Result.Err({ why: "no-constraints-selected" });
    }

    const matchIds = [...this._selectedConstraintMatchIds];

    this.setStatusMessage(
      {
        text: `Placing ${matchIds.length} constraint${
          matchIds.length > 1 ? "s" : ""
        }...`,
        kind: "info",
      },
      0
    );

    const ctx = createRequestContext();
    const result = await this.client.request(ctx, {
      kind: "place-with-symmetry",
      data: {
        designIdent: this.activeIdent,
        constraintMatchIds: matchIds,
      },
    });

    if (result.err) {
      this.setStatusMessage(
        {
          text: `Place failed: ${extractErrorMessage(result.val)}`,
          kind: "error",
        },
        5000
      );
      return Result.Err({ why: result.val.why });
    }

    this.setStatusMessage({ text: "Placement complete", kind: "info" }, 3000);
    return Result.Ok(undefined);
  }

  // Route nets symmetrically based on constraints.
  // Only available when viewing a layout with a linked schematic.
  async routeConstraints(): Promise<Result<void, { why: string }>> {
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const editor = this.getActiveEditor();
    if (!editor || editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }

    this.setStatusMessage(
      { text: "Routing constraints symmetrically...", kind: "info" },
      0
    );

    const ctx = createRequestContext();
    const result = await this.client.request(ctx, {
      kind: "route-with-constraints",
      data: { designIdent: this.activeIdent },
    });

    if (result.err) {
      this.setStatusMessage(
        {
          text: `Route failed: ${extractErrorMessage(result.val)}`,
          kind: "error",
        },
        5000
      );
      return Result.Err({ why: result.val.why });
    }

    this.setStatusMessage(
      { text: "Symmetric routing complete", kind: "info" },
      3000
    );
    return Result.Ok(undefined);
  }

  // Select a constraint match in the ConstraintsPanel.
  // Without shift: clears previous selection and selects only this match.
  // With shift: toggles this match in the existing selection.
  selectConstraintMatch(matchId: number, shiftKey: boolean): void {
    if (shiftKey) {
      // Toggle: add if not present, remove if present
      if (this._selectedConstraintMatchIds.has(matchId)) {
        this._selectedConstraintMatchIds.delete(matchId);
      } else {
        this._selectedConstraintMatchIds.add(matchId);
      }
    } else {
      // Replace selection with just this match
      this._selectedConstraintMatchIds.clear();
      this._selectedConstraintMatchIds.add(matchId);
    }
    this.updateSnapshot();
  }

  // Clear all constraint match selections.
  clearConstraintSelection(): void {
    if (this._selectedConstraintMatchIds.size === 0) return;
    this._selectedConstraintMatchIds.clear();
    this.updateSnapshot();
  }

  // Highlight layout elements related to an LVS error.
  // Uses Editor's highlightLvsError API which handles content type checking internally.
  // Only highlights if the active editor matches the layout that LVS was run on.
  highlightLvsError(error: LvsError | null): void {
    const editor = this.getActiveEditor();
    if (!editor) return;

    // Allow clearing highlights without ident verification
    if (error === null) {
      editor.highlightLvsError(null);
      return;
    }

    // Only highlight if we can verify the active layout matches the one LVS was run on
    const lvsIdentKey = this._lvsResult?.identKey;
    if (!lvsIdentKey || !this.activeIdent) return;
    if (Ident.stringify(this.activeIdent) !== lvsIdentKey) return;

    // LvsError (IDE) is structurally compatible with LvsErrorInfo (EditorCore).
    // TypeScript uses structural typing: matching field shapes = compatible types.
    editor.highlightLvsError(error);
  }

  // Highlight layout elements related to a DRC error.
  // Uses Editor's highlightDrcError API which handles content type checking internally.
  // Only highlights if the active editor matches the layout that DRC was run on.
  highlightDrcError(error: DrcError | null): void {
    const editor = this.getActiveEditor();
    if (!editor) return;

    // Allow clearing highlights without ident verification
    if (error === null) {
      editor.highlightDrcError(null);
      return;
    }

    // Only highlight if we can verify the active layout matches the one DRC was run on
    const drcIdentKey = this._drcResult?.identKey;
    if (!drcIdentKey || !this.activeIdent) return;
    if (Ident.stringify(this.activeIdent) !== drcIdentKey) return;

    // DrcError (IDE) is structurally compatible with DrcErrorInfo (EditorCore).
    editor.highlightDrcError(error);
  }

  // Get the current PEX (parasitic extraction) result.
  // Returns null if no PEX has been run.
  getPexResult(): ParasiticsResult | null {
    return this._pexResult;
  }

  // Set PEX result.
  // Called by runPex() when extraction completes.
  // Automatically switches to the Parasitics panel when results arrive.
  setPexResult(result: ParasiticsResult | null): void {
    this._pexResult = result;
    this._contentVersion++;
    this.updateSnapshot();

    // Switch to the Parasitics panel when results arrive
    if (result !== null) {
      const pexPanel = this.dockApi?.getPanel("parasitics");
      if (pexPanel) {
        pexPanel.api.setActive();
      }
    }
  }

  // Update the WebSocket connection state. Called by main.tsx from
  // AlphaClient.onConnectionEvent.
  setConnected(isConnected: boolean): void {
    if (this._isConnected === isConnected) {
      return;
    }
    this._isConnected = isConnected;
    this.updateSnapshot();
  }

  // Show the "Connection Lost — Reload?" modal. Idempotent: once shown the
  // first reason is preserved (subsequent calls are ignored) so the user
  // doesn't see the message text flicker as the client discovers more
  // detail about the failure.
  showReconnectDialog(reason: ReconnectDialogState["reason"]): void {
    if (this._reconnectDialog !== null) {
      return;
    }
    this._reconnectDialog = { reason };
    this.updateSnapshot();
  }

  // Append a message to the in-app console panel (BottomPanel) and mirror
  // it to the browser DevTools console with a matching severity. Callers
  // should not prefix the message with a tag — `source` is rendered separately
  // by BottomPanel and is used as the mirror prefix here. Freeform message
  // text is preferred over structured objects because the panel renders text.
  //
  // The in-app log is bounded by CONSOLE_LOG_LIMIT; oldest entries drop off.
  logToConsole(level: ConsoleLogLevel, source: string, message: string): void {
    this._consoleLogIdCounter += 1;
    const entry: ConsoleLogEntry = {
      id: this._consoleLogIdCounter,
      timestamp: Date.now(),
      level,
      source,
      message,
    };
    const next = this._consoleLog.concat(entry);
    this._consoleLog =
      next.length > Workspace.CONSOLE_LOG_LIMIT
        ? next.slice(next.length - Workspace.CONSOLE_LOG_LIMIT)
        : next;

    // Mirror to DevTools so both surfaces have the same record. Use the
    // matching severity so the browser's filter controls work as expected.
    const tagged = `[${source}] ${message}`;
    if (level === "error") {
      console.error(tagged);
    } else if (level === "warn") {
      console.warn(tagged);
    } else if (level === "debug") {
      console.debug(tagged);
    } else {
      console.log(tagged);
    }

    this.updateSnapshot();
  }

  // Run PEX (parasitic extraction) on the active layout.
  // Returns a Result indicating success or failure.
  // The PEX results are stored in workspace state and displayed in the Parasitics panel.
  // Errors are displayed via the status bar.
  async runPex(): Promise<Result<void, { why: string }>> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err({ why: "no-active-editor" });
    }
    if (editor.content.contentKind !== "layout") {
      return Result.Err({ why: "not-a-layout" });
    }
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-ident" });
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "run-pex",
      data: { designIdent: this.activeIdent },
    });

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      this.setStatusMessage(
        { text: `PEX failed: ${errorMsg}`, kind: "error" },
        5000
      );
      return Result.Err({ why: errorMsg });
    }

    // Validate and convert the result
    // The server returns camelCase field names from Rust
    const validationResult = PexResultResponse.validator.validate(result.val);
    if (validationResult.err) {
      this.setStatusMessage(
        { text: "PEX result validation failed", kind: "error" },
        5000
      );
      return Result.Err({ why: "invalid-pex-response" });
    }

    const pexResponse = validationResult.val;

    // Convert to ParasiticsResult format used by the panel
    const parasiticsResult: ParasiticsResult = {
      identKey: JSON.stringify(this.activeIdent),
      totalWireResistance: pexResponse.totalWireResistanceOhms,
      totalViaResistance: pexResponse.totalViaResistanceOhms,
      totalGroundCapacitance: pexResponse.totalGroundCapacitanceFf,
      totalCouplingCapacitance: pexResponse.totalCouplingCapacitanceFf,
      netSummaries: pexResponse.netDetails.map((nd) => ({
        name: nd.netName,
        wireResistance: nd.wireResistanceOhms,
        viaResistance: nd.viaResistanceOhms,
        groundCapacitance: nd.groundCapacitanceFf,
        couplingCapacitance: nd.couplingCapacitanceFf,
        segmentCount: nd.segmentCount,
        viaCount: nd.viaCount,
      })),
      topCouplingPairs: pexResponse.topCouplingPairs.map((cp) => ({
        netA: cp.netA,
        netB: cp.netB,
        capacitance: cp.capacitanceFf,
      })),
      stats: pexResponse.stats,
    };

    this.setPexResult(parasiticsResult);

    return Result.Ok(undefined);
  }

  // Highlight routes belonging to specific net(s) in the layout.
  // Pass null to clear highlights.
  // Pass one net name to highlight a single net.
  // Pass two net names to highlight a coupling pair.
  highlightParasiticsNet(netA: string | null, netB?: string): void {
    const editor = this.getActiveEditor();
    if (!editor) return;

    // Clear highlights
    if (netA === null) {
      editor.highlightParasiticsNet(null);
      return;
    }

    // Only highlight if active editor is a layout
    if (editor.content.contentKind !== "layout") return;

    // Highlight the net(s)
    if (netB) {
      editor.highlightParasiticsNet(netA, netB);
    } else {
      editor.highlightParasiticsNet(netA);
    }
  }

  // Look up a container div by stringified ident key.
  // Used by EditorTabPanel (dockview panel content) to attach a specific editor.
  getContainerForKey(key: string): HTMLDivElement | null {
    const entry = this.findEditorByKey(key);
    return entry?.container ?? null;
  }

  // Look up an Editor instance by stringified ident key.
  getEditorForKey(key: string): Editor | null {
    const entry = this.findEditorByKey(key);
    return entry?.editor ?? null;
  }

  // Find an EditorEntry by its stringified ident key.
  private findEditorByKey(key: string): EditorEntry | undefined {
    for (const entry of this.editors.values()) {
      if (Ident.stringify(entry.ident) === key) return entry;
    }
    return undefined;
  }

  // Find the dockview group where editor panels live.
  // First checks existing editor panels, then falls back to the "editor-area"
  // placeholder panel that reserves the center space.
  private findEditorGroup() {
    for (const ident of this.tabOrder) {
      const panel = this.dockApi?.getPanel(Ident.stringify(ident));
      if (panel?.group) return panel.group;
    }
    // Fall back to the placeholder panel that holds the center area
    const placeholder = this.dockApi?.getPanel("editor-area");
    return placeholder?.group;
  }

  // Capture the active editor's canvas as a base64-encoded PNG.
  // Used by the get-img Copilot tool to let the LLM "see" the current design.
  // NOTE: Captures the *active* editor only — if the user switches tabs while
  // a prompt is in flight, the capture may not match the design Copilot is working on.
  // Future improvement: accept an ident to capture a specific editor's canvas.
  captureCanvas(): Result<string, string> {
    const editor = this.getActiveEditor();
    if (!editor) {
      return Result.Err("no-active-editor");
    }
    if (!editor.canvas.toDataURL) {
      return Result.Err("canvas-capture-not-supported");
    }
    const result = editor.canvas.toDataURL();
    if (result.err) {
      return Result.Err(result.val.why);
    }
    return result;
  }

  // Open the create-layout dialog (stack selection + destination) and wait.
  // Returns { stackId, destination } or null if cancelled.
  promptCreateLayout(
    stacks: string[],
    defaultPath: string,
    defaultOa: GenerateFromSourceDialogState["defaultOa"]
  ): Promise<GenerateFromSourceDialogResult | null> {
    return new Promise((resolve) => {
      this.generateFromSourceDialogResolve = resolve;
      this.generateFromSourceDialogState = { stacks, defaultPath, defaultOa };
      this.updateSnapshot();
    });
  }

  // Called by the React dialog when the user confirms or cancels.
  resolveGenerateFromSourceDialog(
    result: GenerateFromSourceDialogResult | null
  ): void {
    const resolve = this.generateFromSourceDialogResolve;
    this.generateFromSourceDialogResolve = null;
    this.generateFromSourceDialogState = null;
    this.updateSnapshot();
    resolve?.(result);
  }

  // Show overwrite confirmation dialog. Returns true if user confirms.
  promptOverwrite(designPath: string, detail?: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.overwriteDialogResolve = resolve;
      this.overwriteDialogState = { designPath, detail };
      this.updateSnapshot();
    });
  }

  resolveOverwriteDialog(confirmed: boolean): void {
    const resolve = this.overwriteDialogResolve;
    this.overwriteDialogResolve = null;
    this.overwriteDialogState = null;
    this.updateSnapshot();
    resolve?.(confirmed);
  }

  // Fetch the list of available designs from AlphaServer.
  // Returns only hierpath idents (the depot tree format).
  async listContent(): Promise<Result<schema.HierPathIdent[]>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, { kind: "list-content" });
    const validated = result.andThen(IdentListValidator.validator.validate);
    if (validated.err) return validated;
    return Result.Ok(
      validated.val.filter(
        (id): id is schema.HierPathIdent => id.kind === "hierpath"
      )
    );
  }

  // Check if a design exists in the depot.
  async contentExistsDepot(ident: Ident): Promise<boolean> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "content-exists-depot",
      data: { ident },
    });
    if (result.err) return false;
    return result.val === true;
  }

  // Fetch the depot content tree from AlphaServer.
  // Returns pre-built tree of DepotTreeNode[] (libraries and designs).
  async listContentTree(): Promise<Result<DepotTreeNode[]>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "list-content-tree",
    });
    return result.andThen(DepotTreeValidator.validator.validate);
  }

  // Fetch the list of available PDKs from AlphaServer.
  async listPdks(): Promise<Result<string[]>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, { kind: "list-pdks" });
    return result.andThen(StringArrayValidator.validator.validate);
  }

  // Fetch the list of available stacks for a given PDK from AlphaServer.
  async listStacks(pdkId: string): Promise<Result<string[]>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "list-stacks",
      data: pdkId,
    });
    return result.andThen(StringArrayValidator.validator.validate);
  }

  // Fetch the OpenAccess catalog from AlphaServer.
  // Returns the catalog containing libraries, cells, and views.
  // Returns empty catalog if OpenAccess is not enabled for this tenant.
  async listOACatalog(): Promise<Result<OACatalog>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, { kind: "list-oa-catalog" });
    return result.andThen(OACatalogValidator.validator.validate);
  }

  // What technology an OpenAccess library resolves, and its via definitions.
  // Err means the lookup failed (gateway unreachable, library not in lib.defs);
  // a library with no technology is Ok with an empty `techLib`.
  async oaTech(lib: string): Promise<Result<OaTechInfo>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "oa-tech",
      data: { lib },
    });
    return result.andThen(OaTechInfoValidator.validator.validate);
  }

  // Create a new empty layout in Depot. Returns the ident of the new design.
  // `path` is the full design path (arbitrary depth); the type is recorded by
  // contentKind, not by a path segment.
  async createNewLayout(params: {
    path: string[];
    pdkId: string;
    stackId: string;
  }): Promise<Result<schema.Ident>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "create-new-layout",
      data: params,
    });
    return result.andThen(IdentValidator.validator.validate);
  }

  // Create a new empty schematic in Depot. Returns the ident of the new design.
  // See createNewLayout for how `path` relates to type.
  async createNewSchematic(params: {
    path: string[];
    pdkId?: string;
  }): Promise<Result<schema.Ident>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "create-new-schematic",
      data: params,
    });
    return result.andThen(IdentValidator.validator.validate);
  }

  // Create a new empty symbol in Depot. Returns the ident of the new design.
  // See createNewLayout for how `path` relates to type.
  async createNewSymbol(params: {
    path: string[];
    pdkId?: string;
  }): Promise<Result<schema.Ident>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "create-new-symbol",
      data: params,
    });
    return result.andThen(IdentValidator.validator.validate);
  }

  // Handle a request to open a design by Ident. Brackets the load in the
  // heartbeat busy-guard so a large hierarchy that blocks the server or this
  // client past the pong deadline isn't mistaken for a dead connection. The
  // window spans both the HTTP fetch and the synchronous build below.
  async handleOpenRequest(
    ident: Ident,
    opts: { promptOnUnsavedSession?: boolean } = {}
  ): Promise<void> {
    // Recover-vs-fresh gate, opt-in for user-initiated opens only (the depot
    // tree). If this design isn't already open here, a grace-windowed unsaved
    // session may be sitting on the server (e.g. we reloaded the window
    // mid-edit); ask the server and, if so, prompt and defer the actual open
    // to the dialog's choice. Programmatic callers (LM design-open, generate,
    // hierarchy navigation) leave `opts` empty and open directly — they must
    // not surface a modal or resolve without having opened, since several
    // markDirty the design immediately after awaiting this.
    if (opts.promptOnUnsavedSession && !this.editors.has(ident)) {
      const ctx = createRequestContext(this.userId);
      const status = await this.client.request(ctx, {
        kind: "session-status",
        data: ident,
      });
      if (status.ok && status.val === true) {
        this.openRecoverSessionDialog(ident);
        return;
      }
    }
    await this.openDesignNow(ident);
  }

  // The actual open: fetch content, create the editor, load it. Bracketed in
  // the heartbeat busy-guard so the (possibly long) HTTP fetch + synchronous
  // build isn't mistaken for a dead connection and blamed for a missed pong.
  private async openDesignNow(ident: Ident): Promise<void> {
    this.client.beginBusy();
    try {
      await this.handleOpenRequestImpl(ident);
    } finally {
      this.client.endBusy();
    }
  }

  // Fetches content from AlphaServer, creates an Editor, and loads the content.
  private async handleOpenRequestImpl(ident: Ident): Promise<void> {
    logger.log(`[Workspace] Opening from server: ${JSON.stringify(ident)}`);

    // Close existing tab for this ident so we load fresh content.
    // Without this, workspace.open() short-circuits and activates the stale tab.
    // Preserve dirty state across the close/reopen — close() clears dirtyIdents,
    // but the design may still have unsaved changes in the state manager.
    const key = Ident.stringify(ident);
    const wasDirty = this.dirtyIdents.has(key);
    if (this.editors.has(ident)) {
      this.close(ident);
    }
    if (wasDirty) {
      this.dirtyIdents.add(key);
    }

    const ctx = createRequestContext(this.userId);

    if (USE_BATCH_HIERARCHY_LOAD) {
      // Batch loading: fetch all layouts in the hierarchy with a single request
      const hierarchyResult = await this.client.openHierarchy(ctx, ident);
      if (hierarchyResult.err) {
        logger.error(
          `[Workspace] Failed to open hierarchy: ${extractErrorMessage(
            hierarchyResult.val
          )}`
        );
        return;
      }

      const { contents } = hierarchyResult.val;

      // Server adds root first in the contents array
      const rootContent = contents[0]?.content;
      if (!rootContent) {
        logger.error(`[Workspace] Empty hierarchy response`);
        return;
      }

      // Create the Editor (but don't add the dockview panel yet)
      this.open(ident);

      const entry = this.editors.get(ident);
      if (entry) {
        // Pre-populate the platform's content cache so getContent() hits the cache
        // during recursive Layout.fromSchema instead of making HTTP requests.
        const cacheStats = entry.platform.populateCache(contents);
        logger.log(
          `[Workspace] Populated cache: ${cacheStats.cached} layouts cached, ${cacheStats.failed} failed`
        );

        // Load content into the editor using the root content
        const loadResult = await entry.editor.loadMainContent(rootContent);

        if (loadResult.err) {
          logger.error(
            `[Workspace] Failed to load content: ${extractErrorMessage(
              loadResult.val
            )}`
          );
          entry.platform.clearCache();
          this.close(ident);
          return;
        }

        // Content loaded — notify subscribers (e.g. ConstraintsPanel) so they
        // can read the now-populated editor content.
        this._contentVersion++;
        this._constraintsVersion++;
        this.updateSnapshot();

        // Clear the cache after loading is complete to free memory
        entry.platform.clearCache();
      }

      // Now add the dockview panel and activate it so it scrolls into view
      this.addDockviewPanel(ident);
      this.activate(ident);
    } else {
      // Sequential loading (fallback when USE_BATCH_HIERARCHY_LOAD is false):
      // Fetch root content, then Layout.fromSchema() recursively loads children.
      // 120s timeout: large hierarchies trigger many sequential GCS fetches.
      const result = await this.client.request(
        ctx,
        { kind: "open", data: { kind: "ident", ident } },
        120_000
      );
      if (result.err) {
        logger.error(
          `[Workspace] Failed to open: ${extractErrorMessage(result.val)}`
        );
        return;
      }

      // Create the Editor (but don't add the dockview panel yet)
      this.open(ident);

      // Load content into the editor before adding the dockview panel.
      // This ensures that when React mounts EditorTabPanel and calls fit(),
      // the content is already loaded and the viewport is computed correctly.
      const entry = this.editors.get(ident);
      if (entry) {
        const loadResult = await entry.editor.loadMainContent(result.val);
        if (loadResult.err) {
          logger.error(
            `[Workspace] Failed to load content: ${extractErrorMessage(
              loadResult.val
            )}`
          );
          this.close(ident);
          return;
        }

        // Content loaded — notify subscribers (e.g. ConstraintsPanel) so they
        // can read the now-populated editor content.
        this._contentVersion++;
        this._constraintsVersion++;
        this.updateSnapshot();
      }

      // Now add the dockview panel — React mounts EditorTabPanel, sizes canvas, fits content.
      this.addDockviewPanel(ident);
      this.activate(ident);
    }
  }

  // Save the active (or specified) design to Depot.
  // Flushes that design's pending changes first so the server has up-to-date state.
  // Clears dirty state on success and shows status feedback.
  async save(ident?: Ident): Promise<Result<unknown>> {
    const targetIdent = ident ?? this.activeIdent;
    if (!targetIdent) {
      return Result.Err({ why: "no-active-design" });
    }

    // Flush any in-flight "changes" requests before saving so the server
    // has applied all pending edits before it reads design state.
    await Promise.allSettled(Array.from(this._pendingChanges));

    // Save from server-side state (content field omitted)
    const ctx = createRequestContext(this.userId);
    const result = await this.requestSave(ctx, targetIdent, targetIdent);

    if (result.err) {
      this.setStatusMessage({ text: "Save failed", kind: "error" }, 5000);
    } else {
      this.dirtyIdents.delete(Ident.stringify(targetIdent));
      this.setStatusMessage({ text: "Saved", kind: "info" }, 2000);
    }

    return result;
  }

  // One save request. When the destination is an OpenAccess cellview that
  // exists but was never read here (a layout generated into, or saved as, an
  // existing name), the gateway refuses to overwrite it blindly; ask, and on a
  // yes send the save again marked as a confirmed overwrite.
  private async requestSave(
    ctx: Context,
    sourceIdent: Ident,
    destIdent: Ident
  ): Promise<Result<unknown>> {
    const send = (overwrite: boolean) =>
      this.client.request(ctx, {
        kind: "save",
        data: {
          sourceIdent,
          dest: { kind: "ident", ident: destIdent },
          ...(overwrite ? { overwrite } : {}),
        },
      });
    const first = await send(false);
    if (first.ok || !hasErrorWhy(first.val, "oa-cellview-exists")) {
      return first;
    }
    const confirmed = await this.promptOverwrite(
      Ident.displayName(destIdent),
      "This is an OpenAccess cellview. Overwriting replaces everything in it, including anything drawn in Custom Compiler."
    );
    return confirmed ? send(true) : first;
  }

  // Save the active design under a new name at an arbitrary parent path.
  // Creates a new design in the Depot with the same contentKind as the active design.
  // On success, keeps the original design open and opens the new design in a new tab.
  async saveAs(
    fullPath: string[],
    destination: SaveAsDestination = { kind: "depot" }
  ): Promise<Result<schema.Ident>> {
    if (!this.activeIdent) {
      return Result.Err({ why: "no-active-design" });
    }

    // Require an active editor so the source content is available to copy.
    if (!this.getActiveEditor()) {
      return Result.Err({ why: "no-active-editor" });
    }

    // A depot target is the full design path verbatim (the content's type is
    // carried by its contentKind, not by a path segment); see
    // `saveAsTargetIdent` for OpenAccess.
    const targetIdent: schema.Ident = saveAsTargetIdent(fullPath, destination);

    // Flush any in-flight "changes" requests before saving so the server
    // has applied all pending edits before it reads design state.
    await Promise.allSettled(Array.from(this._pendingChanges));

    // Call server save endpoint with source and target idents
    const ctx = createRequestContext(this.userId);
    const result = await this.requestSave(ctx, this.activeIdent, targetIdent);

    if (result.err) {
      this.setStatusMessage({ text: "Save As failed", kind: "error" }, 5000);
      return Result.Err({ why: "save-as-failed", data: result.val });
    }

    // Success: update design tree, open the new design in a new tab
    this.handleDesignTreeChanged("design-added");
    this.setStatusMessage(
      {
        text: `Saved as ${Ident.displayName(targetIdent)}`,
        kind: "info",
      },
      3000
    );

    // Close the dialog
    this.closeSaveAsDialog();

    // Open the new design in a new tab
    void this.handleOpenRequest(targetIdent);

    return Result.Ok(targetIdent);
  }

  // Duplicate a depot design to a new full path. Server-side depot-copy reads
  // the source from depot (last-saved state) and writes a new design — the
  // source is untouched, so this works even while the source is open. On
  // success, refreshes the tree and opens the new design.
  async duplicateDesign(
    sourceIdent: schema.Ident,
    fullPath: string[]
  ): Promise<Result<schema.HierPathIdent>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "depot-copy",
      data: { sourceIdent, destPath: fullPath },
    });
    const validated = result.andThen(
      DepotCopyResultValidator.validator.validate
    );
    if (validated.err) {
      const errorMsg = extractErrorMessage(validated.val);
      logger.error(`[Workspace] Failed to duplicate design: ${errorMsg}`);
      this.setStatusMessage(
        { text: `Duplicate failed: ${errorMsg}`, kind: "error" },
        5000
      );
      // `errorMsg` is the already-extracted server reason; surface it as the
      // `why` so the dialog's extractErrorMessage shows it (a string in `data`
      // would not be picked up), rather than a terse "duplicate-failed" tag.
      return Result.Err({ why: errorMsg });
    }

    const copiedIdent = validated.val.copiedIdent;
    // The originating client refreshes its own tree here; other clients refresh
    // via the server's design-added broadcast.
    this.handleDesignTreeChanged("design-added");
    this.setStatusMessage(
      { text: `Duplicated to ${copiedIdent.path.join("/")}`, kind: "info" },
      3000
    );
    // Dialog lifecycle is owned by the dialog component: it calls onClose()
    // (→ closeRenameDuplicateDialog) on success, so we don't close here.
    void this.handleOpenRequest(copiedIdent);
    return Result.Ok(copiedIdent);
  }

  // Rename a depot design to a new full path. The server refuses the rename if
  // the design is open by any client, so guard locally first for a clearer
  // message. On success, refreshes the tree (old path gone, new path added).
  async renameDesign(
    sourceIdent: schema.Ident,
    fullPath: string[]
  ): Promise<Result<schema.HierPathIdent>> {
    // Local pre-check: if this client has the design open, ask the user to
    // close it first (the server enforces this too, across all clients).
    if (this.editors.has(sourceIdent)) {
      const msg = "Close the design before renaming it.";
      this.setStatusMessage({ text: msg, kind: "error" }, 5000);
      return Result.Err({ why: msg });
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "rename",
      data: { sourceIdent, destPath: fullPath },
    });
    const validated = result.andThen(RenameResultValidator.validator.validate);
    if (validated.err) {
      const errorMsg = extractErrorMessage(validated.val);
      logger.error(`[Workspace] Failed to rename design: ${errorMsg}`);
      this.setStatusMessage(
        { text: `Rename failed: ${errorMsg}`, kind: "error" },
        5000
      );
      // `errorMsg` is the already-extracted server reason; surface it as the
      // `why` so the dialog's extractErrorMessage shows it (a string in `data`
      // would not be picked up), rather than a terse "rename-failed" tag.
      return Result.Err({ why: errorMsg });
    }

    const renamedIdent = validated.val.renamedIdent;
    // A rename is one logical tree change locally: the refetch reads the
    // current tree (new path present, old absent), so a single refresh
    // suffices — no need to signal the removal and addition separately.
    this.handleDesignTreeChanged("design-added");
    this.setStatusMessage(
      { text: `Renamed to ${renamedIdent.path.join("/")}`, kind: "info" },
      3000
    );
    // Dialog lifecycle is owned by the dialog component: it calls onClose()
    // (→ closeRenameDuplicateDialog) on success, so we don't close here.
    return Result.Ok(renamedIdent);
  }

  // Open the "Save As" dialog.
  openSaveAsDialog(): void {
    if (!this.activeIdent) {
      this.setStatusMessage(
        { text: "No active design to save", kind: "error" },
        3000
      );
      return;
    }
    this._saveAsDialogOpen = true;
    this.updateSnapshot();
  }

  // Close the "Save As" dialog.
  closeSaveAsDialog(): void {
    this._saveAsDialogOpen = false;
    this.updateSnapshot();
  }

  // Open the "Report an Issue" dialog. Called from CommandCenter (and
  // eventually a bug-icon button next to Help in the chrome). See #2146.
  openReportIssueDialog(): void {
    this._reportIssueDialogOpen = true;
    this.updateSnapshot();
  }

  // Close the "Report an Issue" dialog.
  closeReportIssueDialog(): void {
    this._reportIssueDialogOpen = false;
    this.updateSnapshot();
  }

  // Open the design-path dialog in "create" mode. An optional slash-separated
  // parent path (e.g. a folder chosen via "New Design...") pre-fills the Path
  // field.
  openCreateDesignDialog(initialPath?: string): void {
    this._designNameDialog = { kind: "create", initialPath };
    this.updateSnapshot();
  }

  // Close the design-name dialog.
  closeDesignNameDialog(): void {
    this._designNameDialog = null;
    this.updateSnapshot();
  }

  // Open the Rename/Duplicate dialog for a depot design.
  openDuplicateDialog(ident: schema.HierPathIdent): void {
    this._renameDuplicateDialog = { kind: "duplicate", sourceIdent: ident };
    this.updateSnapshot();
  }

  openRenameDialog(ident: schema.HierPathIdent): void {
    this._renameDuplicateDialog = { kind: "rename", sourceIdent: ident };
    this.updateSnapshot();
  }

  // Close the Rename/Duplicate dialog.
  closeRenameDuplicateDialog(): void {
    this._renameDuplicateDialog = null;
    this.updateSnapshot();
  }

  // Open the "Generate Symbol from Schematic" dialog.
  openGenSymbolFromSchDialog(): void {
    this._genSymbolFromSchDialogOpen = true;
    this.updateSnapshot();
  }

  // Close the "Generate Symbol from Schematic" dialog.
  closeGenSymbolFromSchDialog(): void {
    this._genSymbolFromSchDialogOpen = false;
    this.updateSnapshot();
  }

  // Open the "Graphics Catalogue" dialog.
  openGraphicsCatalogueDialog(): void {
    this._graphicsCatalogueDialogOpen = true;
    this.updateSnapshot();
  }

  // Close the "Graphics Catalogue" dialog.
  closeGraphicsCatalogueDialog(): void {
    this._graphicsCatalogueDialogOpen = false;
    this.updateSnapshot();
  }

  // Open the "Delete Design" confirmation dialog for the given design.
  openDeleteDesignDialog(ident: Ident): void {
    this._deleteDesignIdent = ident;
    this.updateSnapshot();
  }

  // Close the "Delete Design" confirmation dialog without deleting.
  closeDeleteDesignDialog(): void {
    this._deleteDesignIdent = null;
    this.updateSnapshot();
  }

  // Open the schematic picker dialog when a symbol has multiple linked schematics.
  openSchematicPickerDialog(
    schematics: ReadonlyArray<schema.symbol.SchematicImplRef>,
    sourcePdkId?: string
  ): void {
    this._schematicPickerDialog = { schematics, entries: null, sourcePdkId };
    this.updateSnapshot();
    void this.fetchPickerEntries(
      "schematic",
      schematics.map((r) => r.target)
    );
  }

  // Close the schematic picker dialog.
  closeSchematicPickerDialog(): void {
    this._schematicPickerDialog = null;
    this.updateSnapshot();
  }

  // Select a schematic from the picker and navigate to it.
  selectSchematicFromPicker(ident: Ident): void {
    this._schematicPickerDialog = null;
    this.updateSnapshot();
    void this.handleOpenRequest(ident);
  }

  // Open the layout picker dialog when a schematic has multiple linked layouts.
  openLayoutPickerDialog(
    layouts: ReadonlyArray<schema.schematic.LayoutImplRef>,
    sourcePdkId?: string
  ): void {
    this._layoutPickerDialog = { layouts, entries: null, sourcePdkId };
    this.updateSnapshot();
    void this.fetchPickerEntries(
      "layout",
      layouts.map((r) => r.target)
    );
  }

  // Close the layout picker dialog.
  closeLayoutPickerDialog(): void {
    this._layoutPickerDialog = null;
    this.updateSnapshot();
  }

  // Select a layout from the picker and navigate to it.
  selectLayoutFromPicker(ident: Ident): void {
    this._layoutPickerDialog = null;
    this.updateSnapshot();
    void this.handleOpenRequest(ident);
  }

  // Fetch pdkIds for picker entries via get-active-design-info.
  private async fetchPickerEntries(
    kind: "schematic" | "layout",
    idents: schema.Ident[]
  ): Promise<void> {
    const ctx = createRequestContext(this.userId);

    const entries = await Promise.all(
      idents.map(async (ident) => {
        const entry: {
          ident: schema.Ident;
          displayName: string;
          pdkId?: string;
        } = {
          ident,
          displayName: Ident.displayName(ident),
        };
        const result = await this.client.request(ctx, {
          kind: "get-active-design-info",
          data: ident,
        });
        if (result.ok && result.val) {
          const info = result.val as { contentKind: string; pdkId?: string };
          if (info.pdkId) entry.pdkId = info.pdkId;
        }
        return entry;
      })
    );

    if (kind === "schematic" && this._schematicPickerDialog) {
      this._schematicPickerDialog = {
        ...this._schematicPickerDialog,
        entries,
      };
    } else if (kind === "layout" && this._layoutPickerDialog) {
      this._layoutPickerDialog = {
        ...this._layoutPickerDialog,
        entries,
      };
    }
    this.updateSnapshot();
  }

  // Open the "Add Symbol Pin" dialog for the given design at the given canvas position.
  openAddSymbolPinDialog(
    designIdent: Ident,
    position: { x: number; y: number }
  ): void {
    this._addSymbolPinDialog = { designIdent, position };
    this.updateSnapshot();
  }

  // Close the "Add Symbol Pin" dialog.
  closeAddSymbolPinDialog(): void {
    this._addSymbolPinDialog = null;
    this.updateSnapshot();
  }

  // Open the "Add Expression Label" dialog for the given design at the given canvas position.
  openAddExprLabelDialog(
    designIdent: Ident,
    position: { x: number; y: number }
  ): void {
    this._addExprLabelDialog = { designIdent, position };
    this.updateSnapshot();
  }

  // Close the "Add Expression Label" dialog.
  closeAddExprLabelDialog(): void {
    this._addExprLabelDialog = null;
    this.updateSnapshot();
  }

  // Confirm adding a symbol pin — called when the user confirms the Add Pin dialog.
  // Routes the config to the Editor instance for the given design.
  confirmAddSymbolPin(
    designIdent: Ident,
    position: { x: number; y: number },
    config: {
      portName: string;
      width: number;
      dir: schema.core.PortDir;
      shape: string;
      labelPosition?: string;
    }
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to add symbol pin");
      return;
    }
    entry.editor.addSymbolPinWithConfig(position, config);
  }

  // Confirm adding an expression label — called when the user confirms the Add Expr Label dialog.
  // Routes the config to the Editor instance for the given design.
  confirmAddExprLabel(
    designIdent: Ident,
    position: { x: number; y: number },
    config: {
      kind: string;
      name?: string;
      paramName?: string;
    }
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to add expression label");
      return;
    }
    entry.editor.addExprLabelWithConfig(position, config);
  }

  // Open the "Edit Symbol Pin" dialog for the given design and pin data.
  openEditSymbolPinDialog(
    designIdent: Ident,
    pinData: {
      portName: string;
      width: number;
      dir: schema.core.PortDir;
      labelPosition: string;
      id?: number;
    }
  ): void {
    this._editSymbolPinDialog = { designIdent, pinData };
    this.updateSnapshot();
  }

  // Close the "Edit Symbol Pin" dialog.
  closeEditSymbolPinDialog(): void {
    this._editSymbolPinDialog = null;
    this.updateSnapshot();
  }

  // Confirm editing a symbol pin — called when the user confirms the Edit Pin dialog.
  // Routes the config to the Editor instance for the given design.
  confirmEditSymbolPin(
    designIdent: Ident,
    pinId: number,
    config: {
      portName: string;
      width: number;
      dir: schema.core.PortDir;
      labelPosition?: string;
    }
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to edit symbol pin");
      return;
    }
    entry.editor.editSymbolPinWithConfig(pinId, config);
  }

  // Open the "Edit Expression Label" dialog for the given design and label data.
  openEditExprLabelDialog(
    designIdent: Ident,
    labelData: {
      kind: schema.symbol.ExprKind;
      name?: string;
      paramName?: string;
      id: number;
    }
  ): void {
    this._editExprLabelDialog = { designIdent, labelData };
    this.updateSnapshot();
  }

  // Close the "Edit Expression Label" dialog.
  closeEditExprLabelDialog(): void {
    this._editExprLabelDialog = null;
    this.updateSnapshot();
  }

  // Confirm editing an expression label — called when the user confirms the dialog.
  // Routes the config to the Editor instance for the given design.
  confirmEditExprLabel(
    designIdent: Ident,
    labelId: number,
    config: {
      kind: schema.symbol.ExprKind;
      name?: string;
      paramName?: string;
    }
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to edit expression label");
      return;
    }
    entry.editor.editExprLabelWithConfig(labelId, config);
  }

  // Extract available metal layers from a layout design's stack.
  // Used by both the Add and Edit Layout Pin dialogs.
  private getLayoutMetalLayers(
    designIdent: Ident
  ): Array<{ name: string; number: number }> {
    const entry = this.editors.get(designIdent);
    const editor = entry?.editor;
    const layers: Array<{ name: string; number: number }> = [];
    if (editor && editor.content.contentKind === "layout") {
      const layoutContent = editor.content;
      const layoutData = (
        layoutContent as unknown as { data: { stack?: schema.pdk.Stack } }
      ).data;
      const pdk = (layoutContent as unknown as { pdk: schema.pdk.PDK | null })
        .pdk;
      if (layoutData.stack && pdk) {
        // Build a name lookup from PDK layers
        const nameByNumber = new Map<number, string>();
        for (const pdkLayer of pdk.layers) {
          nameByNumber.set(pdkLayer.number, pdkLayer.name);
        }
        // Collect unique layer IDs from the stack's metal layers
        const seen = new Set<number>();
        for (const metal of layoutData.stack.metalLayers) {
          if (seen.has(metal.layer)) continue;
          seen.add(metal.layer);
          const name = nameByNumber.get(metal.layer) ?? `Layer ${metal.layer}`;
          layers.push({ name, number: metal.layer });
        }
      }
    }
    return layers;
  }

  // Open the "Add Layout Pin" dialog for the given design at the given canvas position.
  openAddLayoutPinDialog(
    designIdent: Ident,
    position: { x: number; y: number }
  ): void {
    const layers = this.getLayoutMetalLayers(designIdent);
    this._addLayoutPinDialog = { designIdent, position, layers };
    this.updateSnapshot();
  }

  // Close the "Add Layout Pin" dialog.
  closeAddLayoutPinDialog(): void {
    this._addLayoutPinDialog = null;
    this.updateSnapshot();
  }

  // Confirm adding a layout pin — called when the user confirms the Add Layout Pin dialog.
  confirmAddLayoutPin(
    designIdent: Ident,
    position: { x: number; y: number },
    config: {
      name: string;
      layer: number;
      pinType: schema.core.PinType;
    }
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to add layout pin");
      return;
    }
    entry.editor.addLayoutPinWithConfig(position, config);
  }

  // Open the "Edit Layout Pin" dialog for the given design and pin data.
  openEditLayoutPinDialog(
    designIdent: Ident,
    pinData: {
      name: string;
      layer: number;
      pinType: schema.core.PinType | undefined;
      id: number;
    }
  ): void {
    // Reuse the same layer-extraction logic as openAddLayoutPinDialog
    const layers = this.getLayoutMetalLayers(designIdent);
    this._editLayoutPinDialog = { designIdent, pinData, layers };
    this.updateSnapshot();
  }

  // Close the "Edit Layout Pin" dialog.
  closeEditLayoutPinDialog(): void {
    this._editLayoutPinDialog = null;
    this.updateSnapshot();
  }

  // Confirm editing a layout pin — called when the user confirms the Edit Layout Pin dialog.
  confirmEditLayoutPin(
    designIdent: Ident,
    pinId: number,
    config: {
      name: string;
      layer: number;
      pinType: schema.core.PinType;
    }
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to edit layout pin");
      return;
    }
    entry.editor.editLayoutPinWithConfig(pinId, config);
  }

  // Open the "Configure Power Routing" dialog for the given layout.
  openPowerRoutingDialog(designIdent: Ident): void {
    const entry = this.editors.get(designIdent);
    if (!entry?.editor) {
      logger.warn("No editor found for design to configure power routing");
      return;
    }

    const lc = entry.editor.getLayoutContent();
    if (!lc) {
      logger.warn("No layout content for power routing dialog");
      return;
    }

    // Collect power/ground net names from pins by their pinType
    const powerNets = new Set<string>();
    const groundNets = new Set<string>();

    // First, identify power/ground pins by their pinType
    const powerPinNames = new Set<string>();
    const groundPinNames = new Set<string>();
    for (const pin of lc.layoutEntity.absEntity.pins.values()) {
      const pinType = pin.data.pinType ?? "signal";
      if (pinType === "power") powerPinNames.add(pin.data.name);
      else if (pinType === "ground") groundPinNames.add(pin.data.name);
    }

    // Then extract net names from connectivity (conns)
    const conns = lc.layoutEntity.implEntity?.data.conns ?? [];
    for (const conn of conns) {
      const netName = conn.net;
      // Check if this net has a connected layout-level pin with power/ground type
      for (const port of conn.ports) {
        if (port.kind === "pin") {
          if (powerPinNames.has(port.pin)) {
            powerNets.add(netName);
          }
          if (groundPinNames.has(port.pin)) {
            groundNets.add(netName);
          }
        }
      }
    }

    // Fallback: if no connectivity info, use pin names directly as net names.
    // This may not match actual connectivity if pins exist but aren't connected.
    if (powerNets.size === 0 && powerPinNames.size > 0) {
      logger.log(
        "[Workspace] No power nets found in connectivity, using power pin names as net names"
      );
      for (const name of powerPinNames) powerNets.add(name);
    }
    if (groundNets.size === 0 && groundPinNames.size > 0) {
      logger.log(
        "[Workspace] No ground nets found in connectivity, using ground pin names as net names"
      );
      for (const name of groundPinNames) groundNets.add(name);
    }

    // Get metal layers from stack (ordered)
    // In v6 schema, layer names are looked up from PDK, not stored on MetalLayer
    const metalLayers: Array<{ name: string; number: number; index: number }> =
      [];
    const stack = lc.stack;
    const pdk = lc.pdk;
    if (stack?.metalLayers) {
      for (let idx = 0; idx < stack.metalLayers.length; idx++) {
        const m = stack.metalLayers[idx];
        // Look up layer name from PDK, or generate a default
        let name = `M${idx + 1}`;
        if (pdk) {
          const pdkLayer = pdk.layers.find((l) => l.number === m.layer);
          if (pdkLayer) {
            name = pdkLayer.name;
          }
        }
        metalLayers.push({
          name,
          number: m.layer,
          index: idx,
        });
      }
    }

    this._powerRoutingDialog = {
      designIdent,
      powerNets: Array.from(powerNets),
      groundNets: Array.from(groundNets),
      metalLayers,
    };
    this.updateSnapshot();
  }

  // Close the "Configure Power Routing" dialog.
  closePowerRoutingDialog(): void {
    this._powerRoutingDialog = null;
    this.updateSnapshot();
  }

  // Run power routing with the given configuration.
  async runPowerRouting(
    designIdent: Ident,
    config: {
      vddNet: string;
      gndNet: string;
      lowestLayerIndex: number;
      highestLayerIndex: number;
      powerTrackPeriod: number;
      groundTrackPeriod: number;
      generateRings: boolean;
    }
  ): Promise<void> {
    const ctx = createRequestContext(this.userId);

    // Get the layout content to extract layer info from the stack
    const entry = this.editors.get(designIdent);
    const lc = entry?.editor?.getLayoutContent();
    const stack = lc?.stack;
    const pdk = lc?.pdk;

    // Validate stack exists
    if (!stack?.metalLayers) {
      this.setStatusMessage(
        { text: "Layout missing metal stack configuration", kind: "error" },
        5000
      );
      return;
    }

    // Validate layer indices before processing
    if (
      config.lowestLayerIndex < 0 ||
      config.highestLayerIndex >= stack.metalLayers.length ||
      config.lowestLayerIndex >= config.highestLayerIndex
    ) {
      logger.error("[Workspace] Invalid layer indices for power routing", {
        lowest: config.lowestLayerIndex,
        highest: config.highestLayerIndex,
        stackSize: stack.metalLayers.length,
      });
      this.setStatusMessage(
        { text: "Invalid layer selection", kind: "error" },
        5000
      );
      return;
    }

    // Build layerConfigs from the selected layer range.
    // Apply track period as a pitch multiplier - the mesh pitch is the native
    // layer pitch multiplied by the track period. Use average of power/ground
    // periods since the mesh alternates between them.
    const avgTrackPeriod = Math.round(
      (config.powerTrackPeriod + config.groundTrackPeriod) / 2
    );
    const layerConfigs: Array<{
      layer: number;
      name?: string;
      width: number;
      pitch: number;
    }> = [];

    for (
      let idx = config.lowestLayerIndex;
      idx <= config.highestLayerIndex;
      idx++
    ) {
      const m = stack.metalLayers[idx];
      // Look up layer name from PDK
      let name: string | undefined;
      if (pdk) {
        const pdkLayer = pdk.layers.find((l) => l.number === m.layer);
        if (pdkLayer) {
          name = pdkLayer.name;
        }
      }
      layerConfigs.push({
        layer: m.layer,
        name,
        width: m.width,
        // Mesh pitch = native pitch * track period
        pitch: m.pitch * avgTrackPeriod,
      });
    }

    // Default ring parameters - TODO: expose as dialog inputs or derive from PDK
    const DEFAULT_RING_SPACING_NM = 1000;
    const DEFAULT_RING_OFFSET_NM = 0;

    // Build the mesh power routing request payload
    const meshConfig = {
      vddNet: config.vddNet,
      gndNet: config.gndNet,
      mesh: {
        layerConfigs,
        alternating: true,
      },
      ring: config.generateRings
        ? { spacing: DEFAULT_RING_SPACING_NM, offset: DEFAULT_RING_OFFSET_NM }
        : undefined,
      powerTrackPeriod: config.powerTrackPeriod,
      groundTrackPeriod: config.groundTrackPeriod,
    };

    // Call the mesh-power-router endpoint via AlphaServer
    const result = await this.client.request(ctx, {
      kind: "mesh-power-router",
      data: {
        designIdent,
        config: meshConfig,
      },
    });

    if (result.err) {
      const errorReason = result.val.why;
      if (!errorReason) {
        // Unexpected error shape - log full error for debugging
        logger.error(
          "[Workspace] Power routing failed with unexpected error shape",
          result.val
        );
        this.setStatusMessage(
          { text: "Power routing failed", kind: "error" },
          5000
        );
        return;
      }
      logger.error(`[Workspace] Failed to run power routing: ${errorReason}`);
      // Map error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        "design-not-found": "Layout not found",
        "no-rust-tool-client": "Tool server not connected",
        "no-active-layout": "No active layout",
        "layout-missing-pdk-or-stack":
          "Layout missing PDK or metal stack configuration",
      };
      const userMessage =
        errorMessages[errorReason] ?? `Power routing failed: ${errorReason}`;
      this.setStatusMessage({ text: userMessage, kind: "error" }, 5000);
      return;
    }

    this.setStatusMessage(
      { text: "Power routing complete", kind: "info" },
      3000
    );

    // Reload the layout to show the new power mesh
    await this.handleOpenRequest(designIdent);
    this.markDirty(designIdent);
  }

  // Delete a design from the depot. Closes the tab if open, then refreshes the tree.
  async deleteDesign(ident: Ident): Promise<void> {
    this._deleteDesignIdent = null;

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "delete",
      data: { ident },
    });

    if (result.err) {
      const errorMsg = extractErrorMessage(result.val);
      logger.error(`[Workspace] Failed to delete design: ${errorMsg}`);
      this.setStatusMessage(
        { text: `Delete failed: ${errorMsg}`, kind: "error" },
        5000
      );
      this.updateSnapshot();
      return;
    }

    // Close the tab if this design is open
    if (this.editors.has(ident)) {
      this.close(ident);
    }

    this.setStatusMessage({ text: "Design deleted", kind: "info" }, 2000);
    this.handleDesignTreeChanged("design-removed");
  }

  // Generate a symbol from the active schematic, then open the result.
  async genSymbolFromSchematic(
    symbolIdent: Ident,
    pdkId?: string
  ): Promise<void> {
    const schematicIdent = this.activeIdent;
    if (!schematicIdent) {
      this.setStatusMessage({ text: "No active design", kind: "error" }, 3000);
      return;
    }

    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "create-symbol-from-schematic",
      data: { schematicIdent, symbolIdent, pdkId },
    });
    const validated = result.andThen(IdentValidator.validator.validate);

    if (validated.err) {
      const errorMsg = extractErrorMessage(validated.val);
      logger.error(`[Workspace] Failed to generate symbol: ${errorMsg}`);
      this.setStatusMessage(
        { text: `Generate symbol failed: ${errorMsg}`, kind: "error" },
        5000
      );
      return;
    }

    await this.handleOpenRequest(validated.val);
    this.setStatusMessage({ text: "Symbol generated", kind: "info" }, 2000);
    this.handleDesignTreeChanged("design-added");
  }

  // Reveal an ident in the Depot tree. Ensures the left panel is visible
  // so the user can see the tree, then sets the reveal target so DesignExplorer
  // can expand ancestors and scroll to the node.
  revealInDepot(ident: schema.Ident): void {
    this._revealInDepotIdent = ident;
    // Activate the Design Explorer tab (showPanel also ensures the
    // left sidebar group is visible if it was collapsed).
    this.showPanel("left");
  }

  // Clear the reveal target after the DesignExplorer has finished revealing.
  clearRevealInDepot(): void {
    this._revealInDepotIdent = null;
    this.updateSnapshot();
  }

  // Reveal an instance in the Hierarchy panel (triggered by context menu "Show in Hierarchy").
  showInHierarchy(instanceName: string, instanceId: number): void {
    this._hierarchyRevealTarget = { name: instanceName, id: instanceId };
    // Activate the Hierarchy panel tab (showPanel also ensures the
    // left sidebar group is visible if it was collapsed).
    // Note: showPanel calls updateSnapshot() internally, which will
    // pick up the _hierarchyRevealTarget we just set above.
    this.showPanel("hierarchy");
  }

  // Clear the hierarchy reveal target after the panel has finished revealing.
  clearHierarchyReveal(): void {
    this._hierarchyRevealTarget = null;
    this.updateSnapshot();
  }

  // Record that `childIdent` was opened from `parentIdent`.
  // Used by "Open Parent" to navigate back.
  setParentBreadcrumb(childIdent: Ident, parentIdent: Ident): void {
    this._parentBreadcrumbs.set(Ident.stringify(childIdent), parentIdent);
  }

  // Open a design and record a parent breadcrumb so the child tab can
  // navigate back via "Open Parent". Centralises the set-then-open pattern
  // used by hierarchy rows and the instance-target action.
  openWithBreadcrumb(targetIdent: Ident, parentIdent: Ident | null): void {
    if (parentIdent) {
      this.setParentBreadcrumb(targetIdent, parentIdent);
    }
    void this.handleOpenRequest(targetIdent);
  }

  // Whether a parent breadcrumb exists for the given ident.
  hasParentBreadcrumb(ident: Ident): boolean {
    return this._parentBreadcrumbs.has(Ident.stringify(ident));
  }

  // Navigate to the parent design that the given ident was opened from.
  // If the parent is already open in a tab, activates it; otherwise opens it.
  openParent(ident: Ident): void {
    const parentIdent = this._parentBreadcrumbs.get(Ident.stringify(ident));
    if (!parentIdent) return;

    // If the parent tab is already open, just activate it
    if (this.editors.has(parentIdent)) {
      this.activate(parentIdent);
    } else {
      void this.handleOpenRequest(parentIdent);
    }
  }

  // Navigate from a symbol design to the schematic(s) that implement it, using
  // the symbol's stored `schematicImpls` forward references.
  openLinkedSchematic(ident: Ident): void {
    const editor = this.editors.get(ident)?.editor ?? this.getActiveEditor();
    if (!editor) return;
    const content = editor.content;
    if (content.contentKind !== "symbol") return;
    this.navigateToSchematicImpls(
      content.render.schematicImpls,
      content.render.pdkId
    );
  }

  // Navigate from a schematic design to the symbol it implements, using the
  // schematic's stored `symbolImpl` back-reference.
  openLinkedSymbol(ident: Ident): void {
    const editor = this.editors.get(ident)?.editor ?? this.getActiveEditor();
    if (!editor) return;
    const content = editor.content;
    if (content.contentKind !== "schematic" || !content.symbolImpl) return;
    void this.handleOpenRequest(content.symbolImpl);
  }

  // Navigate to a symbol's linked schematic(s): open directly when there is a
  // single implementation, otherwise present the schematic picker dialog.
  navigateToSchematicImpls(
    schematicImpls: ReadonlyArray<schema.symbol.SchematicImplRef> | undefined,
    sourcePdkId?: string
  ): void {
    if (!schematicImpls || schematicImpls.length === 0) return;
    if (schematicImpls.length === 1) {
      void this.handleOpenRequest(schematicImpls[0].target);
    } else {
      this.openSchematicPickerDialog(schematicImpls, sourcePdkId);
    }
  }

  // Open the instance dialog in "new" mode for a given design at a canvas position.
  // Immediately opens the dialog with null symbols (loading state),
  // then fetches available symbols from AlphaServer asynchronously.
  openAddInstanceDialog(
    designIdent: Ident,
    position: { x: number; y: number }
  ): void {
    this._instanceDialog = {
      designIdent,
      mode: { kind: "new", position, symbols: null },
      error: null,
    };
    this.updateSnapshot();

    // Fetch symbols asynchronously. First get the design's pdkId so we
    // can filter symbols by PDK.
    void this.fetchSymbolsForDialog(designIdent);
  }

  // Open the "Add Instance" dialog with a specific symbol pre-selected.
  // Skips the symbol picker step and goes directly to the params form.
  // Used by drag-and-drop from PDK primitives section.
  openAddInstanceDialogWithSymbol(
    designIdent: Ident,
    position: { x: number; y: number },
    symbol: SymbolInfo
  ): void {
    this._instanceDialog = {
      designIdent,
      mode: { kind: "new", position, symbols: [], preSelectedSymbol: symbol },
      error: null,
    };
    this.updateSnapshot();
  }

  // Open the instance dialog in "edit" mode for a given instance.
  // Immediately shows the dialog in a loading state, then fetches
  // instance details from AlphaServer asynchronously.
  openEditInstanceDialog(designIdent: Ident, instanceId: string): void {
    this._instanceDialog = {
      designIdent,
      mode: {
        kind: "edit",
        instanceId,
        symbolName: null,
        currentParams: null,
        paramSpec: null,
      },
      error: null,
    };
    this.updateSnapshot();

    // Fetch instance details asynchronously.
    void this.fetchInstanceDetailsForDialog(designIdent, instanceId);
  }

  // Open the instance dialog in "rename" mode for a given instance.
  // Immediately shows the dialog in a loading state, then fetches
  // instance details from AlphaServer to get the current name.
  openRenameInstanceDialog(designIdent: Ident, instanceId: string): void {
    this._instanceDialog = {
      designIdent,
      mode: {
        kind: "rename",
        instanceId,
        currentName: null,
      },
      error: null,
    };
    this.updateSnapshot();

    // Fetch instance details to get the current name.
    void this.fetchInstanceNameForRenameDialog(designIdent, instanceId);
  }

  // Close the instance dialog (either mode).
  closeInstanceDialog(): void {
    this._instanceDialog = null;
    this.updateSnapshot();
  }

  // Open the pin dialog in "new" mode. Internally a 2-step flow:
  // properties (Pin Type + Direction) → name.
  openCreatePinDialog(
    designIdent: Ident,
    position: { x: number; y: number }
  ): void {
    this._pinDialog = { designIdent, mode: { kind: "new", position } };
    this.updateSnapshot();
  }

  // Open the pin dialog in "editProperties" mode. Direction is the only
  // editable property today. Use openRenamePinDialog for name changes.
  openEditPinPropertiesDialog(
    designIdent: Ident,
    pinId: number,
    currentDir: PinDirection
  ): void {
    this._pinDialog = {
      designIdent,
      mode: { kind: "editProperties", pinId, currentDir },
    };
    this.updateSnapshot();
  }

  // Close the pin dialog (any mode).
  closePinDialog(): void {
    this._pinDialog = null;
    this.updateSnapshot();
  }

  // Create a pin in the active schematic. Called by PinDialog on confirm
  // of "new" mode.
  createPin(params: {
    designIdent: schema.Ident;
    name: string;
    direction: PinDirection;
    position: { x: number; y: number };
  }): void {
    const { designIdent, name, direction, position } = params;

    // Find the editor for this design
    const entry = this.editors.get(designIdent);
    if (!entry) {
      logger.error(`[Workspace] createPin: no editor found for design`);
      return;
    }

    const { editor } = entry;
    if (editor.content.contentKind !== "schematic") {
      logger.error(`[Workspace] createPin: not a schematic`);
      return;
    }

    // Add the pin to the schematic
    editor.content.addPinAtPosition({ name, direction, position });
  }

  // Update an existing pin's direction. Called by PinDialog on confirm
  // of "editProperties" mode.
  updatePinDirection(params: {
    designIdent: schema.Ident;
    pinId: number;
    newDir: PinDirection;
  }): void {
    const { designIdent, pinId, newDir } = params;

    const entry = this.editors.get(designIdent);
    if (!entry) {
      logger.error(
        `[Workspace] updatePinDirection: no editor found for design`
      );
      return;
    }

    const { editor } = entry;
    if (editor.content.contentKind !== "schematic") {
      logger.error(`[Workspace] updatePinDirection: not a schematic`);
      return;
    }

    editor.content.updatePinDirectionById(pinId, newDir);
  }

  // Open the pin dialog in "rename" mode. Reuses the same component as
  // create / editProperties.
  openRenamePinDialog(
    designIdent: Ident,
    pinId: number,
    currentName: string
  ): void {
    this._pinDialog = {
      designIdent,
      mode: { kind: "rename", pinId, currentName },
    };
    this.updateSnapshot();
  }

  // Open the "Edit Wire Label" dialog for a given design and wire.
  openEditWireLabelDialog(
    designIdent: Ident,
    wireId: number,
    currentLabel: string
  ): void {
    this._editWireLabelDialog = {
      designIdent,
      wireId,
      currentLabel,
    };
    this.updateSnapshot();
  }

  // Close the "Edit Wire Label" dialog.
  closeEditWireLabelDialog(): void {
    this._editWireLabelDialog = null;
    this.updateSnapshot();
  }

  // Open the "Set Route Width" dialog. `params` come from EditorCore (draw mode
  // or the route context menu) and carry the width bounds and optional routeId.
  openEditRouteWidthDialog(
    designIdent: Ident,
    params: layout.EditRouteWidthParams
  ): void {
    this._editRouteWidthDialog = { designIdent, ...params };
    this.updateSnapshot();
  }

  // Close the "Set Route Width" dialog.
  closeEditRouteWidthDialog(): void {
    this._editRouteWidthDialog = null;
    this.updateSnapshot();
  }

  // Apply a width from the dialog. With a routeId + segmentIndex, updates that
  // segment of the existing route; otherwise sets the pending draw width in
  // DrawRoute mode. `width` is the already-normalized value (grid-snapped/
  // clamped, or null to clear).
  updateRouteWidth(designIdent: Ident, width: number | null): void {
    const editor = this.editors.get(designIdent)?.editor;
    if (!editor) return;
    const dialog = this._editRouteWidthDialog;
    if (dialog?.routeId !== undefined && dialog.segmentIndex !== undefined) {
      editor
        .getLayoutContent()
        ?.updateSegmentWidth(dialog.routeId, dialog.segmentIndex, width);
    } else {
      editor.applyDrawRouteWidth(width);
    }
    this.closeEditRouteWidthDialog();
  }

  // Open the "Recover unsaved session" prompt for a design that has an orphaned
  // unsaved in-memory session on the server (see handleOpenRequest).
  openRecoverSessionDialog(ident: Ident): void {
    this._recoverSessionDialog = {
      ident,
      displayName: Ident.displayName(ident),
    };
    this.updateSnapshot();
  }

  closeRecoverSessionDialog(): void {
    this._recoverSessionDialog = null;
    this.updateSnapshot();
  }

  // "Recover unsaved changes": keep the live in-memory session. openDesignNow
  // resumes it because loadFromSource prefers live shared state over depot.
  recoverUnsavedSession(ident: Ident): void {
    this.closeRecoverSessionDialog();
    void this.openDesignNow(ident);
  }

  // "Open saved version": discard the orphaned session so the subsequent open
  // falls back to the last saved depot content.
  async openSavedVersion(ident: Ident): Promise<void> {
    this.closeRecoverSessionDialog();
    const ctx = createRequestContext(this.userId);
    const discard = await this.client.request(ctx, {
      kind: "discard-session",
      data: ident,
    });
    if (discard.err) {
      logger.error(
        `[Workspace] Failed to discard session for ${Ident.stringify(
          ident
        )}: ${extractErrorMessage(discard.val)}`
      );
      // Fall through and open anyway — worst case we resume the session.
    }
    await this.openDesignNow(ident);
  }

  // Open the "Add Text" annotation dialog for a new annotation at `position`.
  openAddTextAnnotationDialog(
    designIdent: Ident,
    position: { x: number; y: number }
  ): void {
    this._textAnnotationDialog = {
      designIdent,
      currentText: "",
      target: { kind: "new", position },
    };
    this.updateSnapshot();
  }

  // Open the "Edit Text" annotation dialog for an existing annotation.
  openEditTextAnnotationDialog(
    designIdent: Ident,
    elementId: number,
    currentText: string
  ): void {
    this._textAnnotationDialog = {
      designIdent,
      currentText,
      target: { kind: "existing", elementId },
    };
    this.updateSnapshot();
  }

  // Close the "Add/Edit Text" annotation dialog.
  closeTextAnnotationDialog(): void {
    this._textAnnotationDialog = null;
    this.updateSnapshot();
  }

  // Update the label on a wire. Called by the EditWireLabelDialog.
  updateWireLabel(designIdent: Ident, wireId: number, newLabel: string): void {
    const entry = this.editors.get(designIdent);
    if (!entry) {
      logger.warn(
        `[Workspace] No editor found for ${Ident.stringify(designIdent)}`
      );
      return;
    }
    const { editor } = entry;
    if (editor.content.contentKind !== "schematic") {
      logger.warn(`[Workspace] Editor content is not a schematic`);
      return;
    }
    // Apply the wire label change through the schematic content
    editor.content.updateWireLabel(wireId, newLabel);
  }

  // Create a new text annotation on a schematic at the given position.
  addTextAnnotation(
    designIdent: Ident,
    position: { x: number; y: number },
    text: string
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry) {
      logger.warn(
        `[Workspace] No editor found for ${Ident.stringify(designIdent)}`
      );
      return;
    }
    const { editor } = entry;
    if (editor.content.contentKind !== "schematic") {
      logger.warn(`[Workspace] Editor content is not a schematic`);
      return;
    }
    editor.content.addTextAnnotation(position, text);
  }

  // Update (or, with empty text, remove) an existing text annotation on a schematic.
  updateTextAnnotation(
    designIdent: Ident,
    elementId: number,
    text: string
  ): void {
    const entry = this.editors.get(designIdent);
    if (!entry) {
      logger.warn(
        `[Workspace] No editor found for ${Ident.stringify(designIdent)}`
      );
      return;
    }
    const { editor } = entry;
    if (editor.content.contentKind !== "schematic") {
      logger.warn(`[Workspace] Editor content is not a schematic`);
      return;
    }
    editor.content.updateTextAnnotation(elementId, text);
  }

  // Open the "Manage Linkages" dialog for the active design.
  openManageLinkagesDialog(): void {
    const ident = this.activeIdent;
    if (!ident) return;
    this._manageLinkagesDialog = {
      designIdent: ident,
      linkages: null,
      error: null,
    };
    this.updateSnapshot();
    void this.fetchLinkages(ident);
  }

  // Close the "Manage Linkages" dialog.
  closeManageLinkagesDialog(): void {
    this._manageLinkagesDialog = null;
    this.updateSnapshot();
  }

  // Fetch linkage info from AlphaServer and update the dialog state.
  private async fetchLinkages(designIdent: Ident): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "get-design-linkages",
      data: { designIdent },
    });
    const validated = result.andThen(LinkageInfoValidator.validator.validate);

    if (validated.err) {
      this._manageLinkagesDialog = this._manageLinkagesDialog && {
        ...this._manageLinkagesDialog,
        error: `Failed to load linkages: ${validated.val.why}`,
      };
    } else {
      this._manageLinkagesDialog = this._manageLinkagesDialog && {
        ...this._manageLinkagesDialog,
        linkages: validated.val,
        error: null,
      };
    }
    this.updateSnapshot();
  }

  // Link a design to the active design. Called by ManageLinkagesDialog.
  async linkDesign(targetPath: string): Promise<void> {
    if (!this._manageLinkagesDialog) return;
    const sourceIdent = this._manageLinkagesDialog.designIdent;
    const ctx = createRequestContext(this.userId);

    const result = await this.client.request(ctx, {
      kind: "link-design",
      data: { sourceIdent, targetPath },
    });
    const validated = result.andThen(LinkageInfoValidator.validator.validate);

    if (validated.err) {
      this._manageLinkagesDialog = {
        ...this._manageLinkagesDialog,
        error: `Link failed: ${validated.val.why}`,
      };
      this.updateSnapshot();
      return;
    }

    this._manageLinkagesDialog = {
      ...this._manageLinkagesDialog,
      linkages: validated.val,
      error: null,
    };
    this.updateSnapshot();
  }

  // Unlink a design from the active design. Called by ManageLinkagesDialog.
  async unlinkDesign(targetIdent: Ident): Promise<void> {
    if (!this._manageLinkagesDialog) return;
    const sourceIdent = this._manageLinkagesDialog.designIdent;
    const ctx = createRequestContext(this.userId);

    const result = await this.client.request(ctx, {
      kind: "unlink-design",
      data: { sourceIdent, targetIdent },
    });
    const validated = result.andThen(LinkageInfoValidator.validator.validate);

    if (validated.err) {
      this._manageLinkagesDialog = {
        ...this._manageLinkagesDialog,
        error: `Unlink failed: ${validated.val.why}`,
      };
      this.updateSnapshot();
      return;
    }

    this._manageLinkagesDialog = {
      ...this._manageLinkagesDialog,
      linkages: validated.val,
      error: null,
    };
    this.updateSnapshot();
  }

  // Navigate to a linked design. Closes the dialog and opens the target.
  async goToLinkage(ident: Ident): Promise<void> {
    this.closeManageLinkagesDialog();
    await this.handleOpenRequest(ident);
  }

  // Rename a pin in the active schematic. Called by RenamePinDialog when the user confirms.
  renamePin(params: {
    designIdent: schema.Ident;
    pinId: number;
    newName: string;
  }): void {
    const { designIdent, pinId, newName } = params;

    // Find the editor for this design
    const entry = this.editors.get(designIdent);
    if (!entry) {
      logger.error(`[Workspace] renamePin: no editor found for design`);
      return;
    }

    const { editor } = entry;
    if (editor.content.contentKind !== "schematic") {
      logger.error(`[Workspace] renamePin: not a schematic`);
      return;
    }

    // Rename the pin in the schematic by ID
    editor.content.renamePinById(pinId, newName);
  }

  // Fetch available symbols from AlphaServer and update the dialog state.
  private async fetchSymbolsForDialog(designIdent: Ident): Promise<void> {
    const ctx = createRequestContext(this.userId);

    // Load the design content to determine its pdkId
    const contentResult = await this.client.request(ctx, {
      kind: "open",
      data: { kind: "ident", ident: designIdent },
    });

    let pdkId: string | undefined;
    if (!contentResult.err) {
      const val = contentResult.val;
      if (
        typeof val === "object" &&
        val !== null &&
        "pdkId" in val &&
        typeof (val as Record<string, unknown>).pdkId === "string"
      ) {
        pdkId = (val as Record<string, unknown>).pdkId as string;
      }
    }

    // Request available symbols, filtered by PDK if available.
    // Pass the design ident so the server can determine the correct content kind.
    const result = await this.client.request(ctx, {
      kind: "list-available-symbols",
      data: { pdkId, ident: designIdent },
    });

    // Dialog may have been closed while we were fetching
    if (!this._instanceDialog || this._instanceDialog.mode.kind !== "new") {
      return;
    }

    const validated = result.andThen(
      SymbolInfoArrayValidator.validator.validate
    );
    if (validated.err) {
      this._instanceDialog = {
        ...this._instanceDialog,
        error: extractErrorMessage(validated.val),
      };
    } else {
      this._instanceDialog = {
        ...this._instanceDialog,
        mode: { ...this._instanceDialog.mode, symbols: validated.val },
      };
    }
    this.updateSnapshot();
  }

  // Fetch instance details from AlphaServer and update the dialog state.
  private async fetchInstanceDetailsForDialog(
    designIdent: Ident,
    instanceId: string
  ): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "get-instance",
      data: { designIdent, instanceId },
    });

    // Dialog may have been closed while we were fetching.
    if (!this._instanceDialog || this._instanceDialog.mode.kind !== "edit") {
      return;
    }

    const validated = result.andThen(
      InstanceDetailsValidator.validator.validate
    );
    if (validated.err) {
      this._instanceDialog = {
        ...this._instanceDialog,
        error: extractErrorMessage(validated.val),
      };
    } else {
      const instance = validated.val;
      if (!instance.params || !instance.paramSpec) {
        this._instanceDialog = {
          ...this._instanceDialog,
          mode: {
            ...this._instanceDialog.mode,
            symbolName: instance.symbolName,
          },
          error: "This instance does not have editable parameters",
        };
      } else {
        this._instanceDialog = {
          ...this._instanceDialog,
          mode: {
            ...this._instanceDialog.mode,
            symbolName: instance.symbolName,
            currentParams: instance.params,
            paramSpec: instance.paramSpec,
            currentRectangular: instance.rectangular,
          },
        };
      }
    }
    this.updateSnapshot();
  }

  // Fetch instance name from AlphaServer for the rename dialog.
  private async fetchInstanceNameForRenameDialog(
    designIdent: Ident,
    instanceId: string
  ): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "get-instance",
      data: { designIdent, instanceId },
    });

    // Dialog may have been closed while we were fetching.
    if (!this._instanceDialog || this._instanceDialog.mode.kind !== "rename") {
      return;
    }

    const validated = result.andThen(
      InstanceDetailsValidator.validator.validate
    );
    if (validated.err) {
      this._instanceDialog = {
        ...this._instanceDialog,
        error: extractErrorMessage(validated.val),
      };
    } else {
      this._instanceDialog = {
        ...this._instanceDialog,
        mode: {
          ...this._instanceDialog.mode,
          currentName: validated.val.name,
        },
      };
    }
    this.updateSnapshot();
  }

  // List available symbols from AlphaServer (for the instance dialog).
  async listAvailableSymbols(pdkId?: string): Promise<Result<SymbolInfo[]>> {
    const ctx = createRequestContext(this.userId);
    // Pass the active design ident so the server can determine the correct content kind.
    const result = await this.client.request(ctx, {
      kind: "list-available-symbols",
      data: { pdkId, ident: this.activeIdent ?? undefined },
    });
    return result.andThen(SymbolInfoArrayValidator.validator.validate);
  }

  // One symbol's generator param spec. `listAvailableSymbols` no longer
  // carries these — see the `get-symbol-param-spec` doc in ServerInterface —
  // so the Add Instance dialog fetches the spec for whichever device the user
  // picks. `null` means the symbol has no generator params (a builtin).
  async getSymbolParamSpec(
    symbolName: string,
    pdkId?: string
  ): Promise<Result<GeneratorParamSpec | null>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "get-symbol-param-spec",
      data: { symbolName, pdkId, ident: this.activeIdent ?? undefined },
    });
    return result.andThen(GeneratorParamSpecOrNullValidator.validator.validate);
  }

  // Create an instance via AlphaServer's create-instance endpoint.
  async createInstance(params: {
    designIdent: schema.Ident;
    symbolName: string;
    pdkId?: string;
    params?: Record<string, unknown>;
    position?: { x: number; y: number };
    name?: string;
    rectangular?: boolean;
  }): Promise<Result<CreateInstanceResult>> {
    const ctx = createRequestContext(this.userId);
    // 120s timeout: create-instance can invoke a generator, and the external
    // (Custom Compiler) generator runs synchronously on the daemon for tens of
    // seconds — the default 10s would abort a valid in-flight generation. Kept a
    // bounded ceiling (not minutes) so a genuinely hung create still fails in
    // reasonable time.
    const result = await this.client.request(
      ctx,
      {
        kind: "create-instance",
        data: params,
      },
      120_000
    );
    const validated = result.andThen(
      CreateInstanceResultValidator.validator.validate
    );
    if (validated.ok) {
      await this.applyServerChangesLocally(
        params.designIdent,
        [validated.val.change],
        "created instance"
      );
      this.selectCreatedInstance(params.designIdent, validated.val.instanceId);
    }
    return validated;
  }

  // Create an instance from a depot design ident (not a generator).
  // Used by "Add To Design" context menu and drag-and-drop from depot tree.
  async createInstanceFromDepot(params: {
    designIdent: schema.Ident;
    targetIdent: schema.Ident;
    position?: { x: number; y: number };
    name?: string;
  }): Promise<Result<CreateInstanceResult>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "create-instance",
      data: {
        designIdent: params.designIdent,
        targetIdent: params.targetIdent,
        position: params.position,
        name: params.name,
      },
    });
    const validated = result.andThen(
      CreateInstanceResultValidator.validator.validate
    );
    if (validated.ok) {
      await this.applyServerChangesLocally(
        params.designIdent,
        [validated.val.change],
        "created instance"
      );
      this.selectCreatedInstance(params.designIdent, validated.val.instanceId);
    }
    return validated;
  }

  // Get instance details from AlphaServer.
  async getInstance(params: {
    designIdent: schema.Ident;
    instanceId: string;
  }): Promise<Result<InstanceDetails>> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "get-instance",
      data: params,
    });
    return result.andThen(InstanceDetailsValidator.validator.validate);
  }

  // Update instance parameters via AlphaServer's update-instance endpoint.
  async updateInstance(params: {
    designIdent: schema.Ident;
    instanceId: string;
    name?: string;
    params?: Record<string, unknown>;
    position?: { x: number; y: number };
    rectangular?: boolean;
  }): Promise<Result<unknown>> {
    const ctx = createRequestContext(this.userId);
    // 120s timeout, matching `createInstance`: a param edit on a generator
    // instance regenerates geometry through the very same path a placement
    // does, so for an external (Custom Compiler) cell it waits on the same
    // tens-of-seconds daemon run. Under the default 10s the edit reported
    // "Request timed out after 10s" while the server went on to finish it.
    const result = await this.client.request(
      ctx,
      {
        kind: "update-instance",
        data: params,
      },
      120_000
    );
    if (result.ok) {
      this.markDirty(params.designIdent);
    }
    return result;
  }

  // Called when the depot tree changes (design saved/added/removed).
  // Increments the version counter so DesignExplorer re-fetches.
  // For "design-saved", the design already exists in the tree — only its
  // content changed, not the tree structure — so we skip the refresh.
  handleDesignTreeChanged(
    kind: "design-saved" | "design-added" | "design-removed"
  ): void {
    if (kind === "design-saved") {
      return;
    }
    this._designTreeVersion++;
    this.updateSnapshot();
  }

  // Subscribe the chat UI to this conversation's streamed progress / answer
  // frames. Persistent for the conversation lifetime: the caller
  // subscribes once and the returned function unsubscribes. Progress is NOT
  // routed per-prompt — it streams via the single per-conversation subscription.
  subscribeAssistantProgress(sink: ProgressCallback): () => void {
    return this.client.subscribeConversation(this.client.conversationId, sink);
  }

  // ─── Agents panel (running-agent hierarchy) ──────────────────────────

  // Subscribe the Agents panel to run-tree updates. Fires immediately with the
  // current tree, then on every push/pull. Returns an unsubscribe fn. Mirrors
  // subscribeAssistantProgress: a dedicated channel so tree churn doesn't bump
  // the whole WorkspaceSnapshot.
  subscribeAgentTree(sink: (tree: AgentTreeNode | null) => void): () => void {
    this.agentTreeListeners.add(sink);
    sink(this._agentTree);
    return () => {
      this.agentTreeListeners.delete(sink);
    };
  }

  // Replace the current agent tree. Called by the push path
  // (client.onAgentTree) and by fetchAgentTree. Null means no live tree.
  setAgentTree(tree: AgentTreeNode | null): void {
    this._agentTree = tree;
    for (const sink of this.agentTreeListeners) sink(tree);
  }

  // Pull the current agent hierarchy (panel open / tab-focus / reconnect).
  // Live updates otherwise arrive via the push path.
  async fetchAgentTree(): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.getAgentTree(ctx);
    if (result.err) {
      // Non-fatal: the panel just keeps its last snapshot. Push will refresh
      // it on the next change.
      console.warn("Failed to fetch agent tree:", result.val);
      return;
    }
    // Response is `AgentTreeNode | null`. Validate the non-null case at the
    // boundary; a null (no live tree) maps straight through.
    if (result.val === null) {
      this.setAgentTree(null);
      return;
    }
    const validated = AgentTreeNode.validator.validate(result.val);
    if (validated.err) {
      // Surface schema drift rather than silently showing an empty tree.
      console.warn("Agent tree failed validation:", validated.val);
      this.setAgentTree(null);
      return;
    }
    this.setAgentTree(validated.val);
  }

  // Stop an agent and its whole subtree. The pane refreshes via push; we also
  // refetch for immediacy.
  async stopAgent(agentId: string): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.stopAgent(ctx, agentId);
    if (result.err) {
      this.setStatusMessage({
        text: `Failed to stop agent: ${extractErrorMessage(result.val)}`,
        kind: "error",
      });
    }
    await this.fetchAgentTree();
  }

  // Retire (evict) a parked agent subtree. The server refuses the root or a
  // subtree with any still-active agent.
  async retireAgent(agentId: string): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.retireAgent(ctx, agentId);
    if (result.err) {
      this.setStatusMessage({
        text: `Failed to retire agent: ${extractErrorMessage(result.val)}`,
        kind: "error",
      });
    }
    await this.fetchAgentTree();
  }

  // Message one agent directly. Parent agents do NOT see this message in their
  // context (the panel warns the user of this before sending).
  async messageAgent(agentId: string, prompt: string): Promise<void> {
    const ctx = createRequestContext(this.userId);
    const result = await this.client.messageAgent(ctx, agentId, prompt);
    if (result.err) {
      this.setStatusMessage({
        text: `Failed to message agent: ${extractErrorMessage(result.val)}`,
        kind: "error",
      });
    }
  }

  // Send a prompt to AlphaServer for the active design.
  // Returns the validated PromptResponse ack or an error. The assistant's
  // streamed answer is rendered separately via subscribeAssistantProgress,
  // not through this call.
  // All editor viewports are locked for the duration of the request to
  // prevent conflicting edits while the LLM is working.
  // Supports prompts with or without an active design.
  async sendPrompt(
    promptText: string,
    images?: ImageAttachment[]
  ): Promise<Result<PromptResponseWire>> {
    const ctx = createRequestContext(this.userId);

    // Generate requestId for tracking and potential cancellation
    this._currentRequestId = crypto.randomUUID();

    this.lockAllEditors();
    try {
      const result = await this.client.prompt(ctx, {
        prompt: promptText,
        designIdent: this.activeIdent ?? undefined,
        requestId: this._currentRequestId,
        images,
      });

      // Validate the response at the data boundary. Design changes arrive
      // separately via the `changes` WebSocket event (handleDesignChanges).
      const validated = result.andThen(PromptResponseWire.validator.validate);
      return validated;
    } finally {
      this._currentRequestId = null;
      this.unlockAllEditors();
    }
  }

  // Apply a server-originated group of changes to the matching editor's
  // content as a single Combo, then mark the design dirty. The server has
  // already applied these (with excludeOrigin=true), so this is how the
  // originating client mirrors them locally and gets them onto the undo
  // stack. If no editor is open for the design (e.g. Copilot edited a
  // layout the user hasn't visited yet), skip — the next loadMainContent
  // will fetch fresh state. `source` is a short label used only in the
  // error log to identify the caller.
  private async applyServerChangesLocally(
    designIdent: schema.Ident,
    changes: Array<unknown>,
    source: string
  ): Promise<void> {
    // A change can reference content the openHierarchy pre-walk never saw (a
    // newly generated cell), so let a cache miss fetch instead of failing.
    this.editors.get(designIdent)?.platform.markCacheIncomplete();
    const editor = this.getEditorForKey(Ident.stringify(designIdent));
    if (editor) {
      const r = await editor.content.applyServerOriginatedChange(changes);
      if (r.err) {
        // Apply failed → local editor state has diverged from the server
        // (which already applied these changes). This is a genuine
        // out-of-sync condition: we recover by re-fetching authoritative
        // state, but it should be rare and is logged as an error so the
        // signal is visible in the logs (tag: STATE-OUT-OF-SYNC).
        logger.error(
          `[Workspace] STATE-OUT-OF-SYNC: failed to apply ${source} locally ` +
            `for ${Ident.stringify(designIdent)}: ${JSON.stringify(r.val)} — ` +
            `forcing full design refresh to recover`
        );
        // handleDesignLoad with loadKind=refresh also marks dirty.
        await this.handleDesignLoad({
          kind: "design-load",
          ident: designIdent,
          contentKind: "",
          loadKind: LoadKind.refresh,
        });
        return;
      }
      // Repaint the canvas with the just-applied changes. applyChange mutates
      // the model but discards the RedrawRequests it produces, so without this
      // the streamed edit isn't drawn until the render loop is next kicked by an
      // interaction (e.g. a click on the window).
      editor.content.requestRedraw();
    }
    this.markDirty(designIdent);
  }

  // Select a just-created instance and enter cursor-attached placement. Called
  // only from the local create path (createInstance / createInstanceFromDepot):
  // collaborative creates arrive elsewhere and must not steal the selection or
  // hijack the cursor, and it's guarded to the active editor. Handles schematic
  // and layout content; a missing entity/editor is a no-op.
  private selectCreatedInstance(
    designIdent: schema.Ident,
    instanceId: string
  ): void {
    const editor = this.getEditorForKey(Ident.stringify(designIdent));
    if (!editor || editor !== this.getActiveEditor()) return;
    const id = parseInt(instanceId, 10);
    if (Number.isNaN(id)) return;

    // Resolve and select the new instance in whichever content kind is active.
    const schematic = editor.getSchematicContent();
    if (schematic) {
      const instance = schematic.findInstance(id);
      if (!instance) return;
      schematic.select(instance);
    } else {
      const layout = editor.getLayoutContent();
      if (!layout) return;
      const instance = layout.layoutEntity.findInstance(id);
      if (!instance) return;
      layout.select(instance);
    }

    // Placement lets the user drag it to the final spot; see enterPlacementMode.
    editor.content.requestRedraw();
    editor.enterPlacementMode();
  }

  // Handle an incremental design-changes event from AlphaServer: apply the
  // streamed changes to the matching editor's canvas. applyServerChangesLocally
  // recovers via a design-load refresh if the local apply fails.
  async handleDesignChanges(msg: DesignChangesMessage): Promise<void> {
    await this.applyServerChangesLocally(
      msg.ident,
      msg.changes,
      "streamed changes"
    );
  }

  // Handle a design-load event from AlphaServer. Brackets the refresh in the
  // heartbeat busy-guard for the same reason as handleOpenRequest: the
  // loadMainContent rebuild can block this client past the pong deadline.
  async handleDesignLoad(msg: DesignLoadMessage): Promise<void> {
    this.client.beginBusy();
    try {
      await this.handleDesignLoadImpl(msg);
    } finally {
      this.client.endBusy();
    }
  }

  // Re-open every currently-open design after a transient reconnect where the
  // server dropped and rebuilt our ClientContext (wired to
  // AlphaClient.onReconnectResume). Each refresh re-fetches via `open`, which
  // returns the still-alive shared state (server grace window) rather than the
  // stale depot copy — so unsaved in-memory edits are preserved and the
  // server's fresh StateManager re-adopts them. loadKind "refresh" preserves
  // zoom/pan. (Refresh marks the design dirty; if it was clean at disconnect
  // that's a harmless over-report until the next save — Phase 2 can refine it
  // once the server reports session dirtiness.)
  async resumeOpenDesignsAfterReconnect(): Promise<void> {
    const idents = [...this.tabOrder];
    logger.log(
      `[Workspace] Resuming ${idents.length} open design(s) after reconnect`
    );
    for (const ident of idents) {
      await this.handleDesignLoad({
        kind: "design-load",
        ident,
        contentKind: "",
        loadKind: LoadKind.refresh,
      });
    }
  }

  // Re-fetches content and reloads the editor canvas, preserving zoom/pan
  // when loadKind is "refresh".
  private async handleDesignLoadImpl(msg: DesignLoadMessage): Promise<void> {
    // Validate the ident at the data boundary
    const identResult = IdentValidator.validator.validate(msg.ident);
    if (identResult.err) {
      logger.error(
        `[Workspace] Invalid ident in design-load event: ${identResult.val.why}`
      );
      return;
    }
    const ident = identResult.val;

    const entry = this.editors.get(ident);
    if (!entry) {
      logger.log(
        `[Workspace] Design-load event for unknown ident: ${JSON.stringify(
          ident
        )}`
      );
      return;
    }

    logger.log(
      `[Workspace] Refreshing editor for ${Ident.displayName(ident)} (${
        msg.loadKind
      })`
    );

    // Re-fetch the content from AlphaServer
    const ctx = createRequestContext(this.userId);
    const result = await this.client.request(ctx, {
      kind: "open",
      data: { kind: "ident", ident },
    });
    if (result.err) {
      logger.error(
        `[Workspace] Failed to refresh: ${extractErrorMessage(result.val)}`
      );
      return;
    }

    // Debug: Log constraint info from refreshed content
    const data = result.val as Record<string, unknown>;
    if (data.contentKind === "schematic") {
      const constraints = data.constraints as
        | { groups?: unknown[]; matches?: unknown[] }
        | undefined;
      logger.log(
        `[Workspace] Refresh received schematic with constraints: groups=${
          constraints?.groups?.length ?? 0
        }, matches=${constraints?.matches?.length ?? 0}`
      );
    }

    // A refresh may have been triggered by CHILD design(s) changing: a resident
    // parent is subscribed to its children and gets an empty-changes design-load
    // naming the changed children in `savedIdents`. Their content was cached by
    // the openHierarchy pre-walk and is otherwise never invalidated, so evict
    // each here — the redraw below then re-resolves them from live server state
    // (Design.open serves in-memory content for resident designs). Without this
    // the parent canvas keeps rendering stale child geometry.
    if (msg.loadKind === "refresh" && Array.isArray(msg.savedIdents)) {
      for (const raw of msg.savedIdents) {
        const childIdent = IdentValidator.validator.validate(raw);
        if (childIdent.ok) entry.platform.evictContent(childIdent.val);
      }
    }

    // Reload the editor with the updated content
    const loadKind =
      msg.loadKind === "refresh" ? LoadKind.refresh : LoadKind.new;
    const loadResult = await entry.editor.loadMainContent(
      result.val,
      loadKind,
      msg.savedIdents
    );
    if (loadResult.err) {
      logger.error(
        `[Workspace] Failed to load refreshed content: ${extractErrorMessage(
          loadResult.val
        )}`
      );
    }

    // "refresh" means state was modified (tool, copilot, multi-user edit) —
    // mark dirty so the UI shows unsaved indicator and auto-save can fire.
    // "new" means fresh load (from depot or rollback) — not dirty.
    if (msg.loadKind === "refresh") {
      this.markDirty(ident);
    }

    // Notify subscribers (e.g. ConstraintsPanel) that content was reloaded
    this._contentVersion++;
    this.updateSnapshot();
  }

  // Reset the auto-save timer. Called on every GUI edit.
  // When the timer fires, all dirty designs are saved to Depot.
  private resetAutoSaveTimer(): void {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      void this.autoSaveAll();
    }, this.autoSaveDelayMs);
  }

  // Auto-save all dirty designs to Depot.
  private async autoSaveAll(): Promise<void> {
    const dirtyKeys = [...this.dirtyIdents];
    for (const key of dirtyKeys) {
      const ident = this.tabOrder.find((i) => Ident.stringify(i) === key);
      if (ident) {
        const result = await this.save(ident);
        if (result.err) {
          logger.error(
            `[Workspace] Auto-save failed for ${key}: ${extractErrorMessage(
              result.val
            )}`
          );
        }
      }
    }
  }

  // Set a transient status message in the status bar.
  // If clearAfterMs is provided, the message auto-clears after that duration.
  // Public so dialogs (e.g. ReportIssueDialog) can surface user-visible
  // confirmation toasts without needing their own UI.
  setStatusMessage(msg: StatusMessage | null, clearAfterMs?: number): void {
    if (this.statusClearTimer) {
      clearTimeout(this.statusClearTimer);
      this.statusClearTimer = null;
    }
    this._statusMessage = msg;
    this.updateSnapshot();

    if (msg && clearAfterMs) {
      this.statusClearTimer = setTimeout(() => {
        this.statusClearTimer = null;
        this._statusMessage = null;
        this.updateSnapshot();
      }, clearAfterMs);
    }
  }

  // Rebuild the immutable snapshot and notify all listeners (React).
  // Custom MIME types for drag-and-drop data.
  private static readonly DEPOT_DND_MIME = "application/x-genalpha-depot-node";
  private static readonly PRIMITIVE_DND_MIME =
    "application/x-genalpha-primitive";

  // Set up drag-and-drop handlers on an editor container so depot designs
  // and PDK primitives can be dropped onto the canvas to create instances.
  private setupDepotDropTarget(
    container: HTMLDivElement,
    editor: Editor,
    ident: Ident
  ): void {
    container.addEventListener("dragover", (e) => {
      const types = e.dataTransfer?.types;
      if (
        !types?.includes(Workspace.DEPOT_DND_MIME) &&
        !types?.includes(Workspace.PRIMITIVE_DND_MIME)
      )
        return;
      e.preventDefault();
      if (!e.dataTransfer) return;
      // Check compatibility with the active design
      const info = this._activeDesignInfo;
      if (!info) {
        e.dataTransfer.dropEffect = "none";
        return;
      }
      e.dataTransfer.dropEffect = "copy";
    });

    container.addEventListener("drop", (e) => {
      // Convert drop position (relative to the container) to world coordinates
      const rect = container.getBoundingClientRect();
      const pageX = e.clientX - rect.left;
      const pageY = e.clientY - rect.top;
      const worldPos = editor.canvas.worldTransforms.toSch({
        x: pageX,
        y: pageY,
      });
      // Snap to the grid, as every in-editor creation path does (they go via
      // `Editor.gridPointNearestToMouse`). `toSch` is a plain affine transform,
      // so at any zoom but 1.0 the drop point is an arbitrary float; created
      // off-grid, an instance stays off-grid, because moves apply a *snapped
      // delta* to the existing location.
      const position = editor.grid.nearestPoint(worldPos);

      // Handle depot design drop
      const depotRaw = e.dataTransfer?.getData(Workspace.DEPOT_DND_MIME);
      if (depotRaw) {
        e.preventDefault();
        let depotNode: {
          ident: schema.HierPathIdent;
          name: string;
          contentKind?: string;
          pdkId?: string;
        };
        try {
          depotNode = JSON.parse(depotRaw) as typeof depotNode;
        } catch {
          return;
        }
        // Prevent adding a design to itself
        if (Ident.equals(ident, depotNode.ident)) {
          return;
        }
        void this.createInstanceFromDepot({
          designIdent: ident,
          targetIdent: depotNode.ident,
          position,
        });
        return;
      }

      // Handle primitive symbol drop
      const primRaw = e.dataTransfer?.getData(Workspace.PRIMITIVE_DND_MIME);
      if (primRaw) {
        e.preventDefault();
        let primData: {
          kind: "primitive";
          symbolName: string;
          pdkId?: string;
          hasParams: boolean;
          symbolInfo?: SymbolInfo;
        };
        try {
          primData = JSON.parse(primRaw) as typeof primData;
        } catch {
          return;
        }
        if (primData.hasParams && primData.symbolInfo) {
          // Parametric primitive: open the params dialog with the symbol pre-selected
          this.openAddInstanceDialogWithSymbol(
            ident,
            position,
            primData.symbolInfo
          );
        } else if (primData.hasParams) {
          // Fallback: open the full add-instance dialog if symbolInfo is missing
          this.openAddInstanceDialog(ident, position);
        } else {
          // Non-parametric: create directly
          void this.createInstance({
            designIdent: ident,
            symbolName: primData.symbolName,
            pdkId: primData.pdkId,
            position,
          });
        }
      }
    });
  }

  // Fetch design info (contentKind, pdkId) from the server and update the snapshot.
  // Fire-and-forget: called after tab switches so the UI can filter the depot tree.
  // Passes the specific activeIdent so the server looks up the correct design
  // (rather than relying on server-side active design tracking).
  private fetchActiveDesignInfo(): void {
    if (!this.activeIdent) {
      // No active design (e.g. the last tab was just closed). Clear the info and
      // publish it — the async branch below is the only other place that pushes a
      // snapshot, so without this the stale info from the closed design would
      // linger in the snapshot.
      this._activeDesignInfo = null;
      this.updateSnapshot();
      return;
    }
    const ctx = createRequestContext(this.userId);
    this.client
      .request(ctx, {
        kind: "get-active-design-info",
        data: this.activeIdent,
      })
      .then((result) => {
        if (result.err) {
          this._activeDesignInfo = null;
        } else {
          // Server returns { contentKind, pdkId } | null
          this._activeDesignInfo = result.val as ActiveDesignInfo | null;
        }
        this.updateSnapshot();
      });
  }

  private updateSnapshot(): void {
    const tabs: TabInfo[] = this.tabOrder.map((ident) => ({
      ident,
      displayName: Ident.displayName(ident),
      isDirty: this.dirtyIdents.has(Ident.stringify(ident)),
    }));
    this.snapshot = Object.freeze({
      tabs,
      activeIdent: this.activeIdent,
      generateFromSourceDialog: this.generateFromSourceDialogState,
      overwriteDialog: this.overwriteDialogState,
      designTreeVersion: this._designTreeVersion,
      contentVersion: this._contentVersion,
      constraintsVersion: this._constraintsVersion,
      hierarchyVersion: this._hierarchyVersion,
      statusMessage: this._statusMessage,
      commandPaletteOpen: this._commandPaletteOpen,
      designNameDialog: this._designNameDialog,
      renameDuplicateDialog: this._renameDuplicateDialog,
      genSymbolFromSchDialogOpen: this._genSymbolFromSchDialogOpen,
      graphicsCatalogueDialogOpen: this._graphicsCatalogueDialogOpen,
      saveAsDialogOpen: this._saveAsDialogOpen,
      reportIssueDialogOpen: this._reportIssueDialogOpen,
      deleteDesignIdent: this._deleteDesignIdent,
      leftPanelVisible: this._leftPanelVisible,
      rightPanelVisible: this._rightPanelVisible,
      bottomPanelVisible: this._bottomPanelVisible,
      isPromptInFlight: this._isPromptInFlight,
      revealInDepotIdent: this._revealInDepotIdent,
      hierarchyRevealTarget: this._hierarchyRevealTarget,
      instanceDialog: this._instanceDialog,
      schematicPickerDialog: this._schematicPickerDialog,
      layoutPickerDialog: this._layoutPickerDialog,
      activeDesignInfo: this._activeDesignInfo,
      addSymbolPinDialog: this._addSymbolPinDialog,
      addExprLabelDialog: this._addExprLabelDialog,
      editSymbolPinDialog: this._editSymbolPinDialog,
      editExprLabelDialog: this._editExprLabelDialog,
      addLayoutPinDialog: this._addLayoutPinDialog,
      editLayoutPinDialog: this._editLayoutPinDialog,
      pinDialog: this._pinDialog,
      editWireLabelDialog: this._editWireLabelDialog,
      editRouteWidthDialog: this._editRouteWidthDialog,
      recoverSessionDialog: this._recoverSessionDialog,
      textAnnotationDialog: this._textAnnotationDialog,
      manageLinkagesDialog: this._manageLinkagesDialog,
      constraintDialog: this._constraintDialog,
      dummyDeviceDialog: this._dummyDeviceDialog,
      collapsedPanels: this._collapsedPanels,
      selectedConstraintMatchIds: new Set(this._selectedConstraintMatchIds),
      powerRoutingDialog: this._powerRoutingDialog,
      isConnected: this._isConnected,
      consoleLog: this._consoleLog,
      reconnectDialog: this._reconnectDialog,
      selection: this.properties.selection,
      selectionVersion: this.properties.selectionVersion,
    });
    for (const listener of this.listeners) {
      listener();
    }
  }
}
